/**
 * Las decisiones del panel, fuera de los componentes.
 *
 * POR QUÉ ESTÁN AQUÍ. Todas ellas vivían dentro de un `.tsx`, mezcladas con el dibujo, y por eso
 * ninguna se podía probar: para comprobar cuál manuscrito propone Analizar había que arrancar un
 * navegador. Sacadas aquí son funciones puras y se prueban en milisegundos — el mismo camino que
 * `humanink-word/addin/src/match.ts`, que existe por lo mismo.
 *
 * Y dos de ellas estaban DUPLICADAS: el orden de los documentos se escribía igual en
 * `Manuscrito.tsx` y en `Chat.tsx`. Dos copias de una regla son dos sitios donde se puede corregir
 * a medias.
 */
import type { Fichero, VueltaLeida } from "./api";

/**
 * El último tramo de una ruta, con barra normal o invertida.
 *
 * En Windows el motor devuelve las rutas con `\`, y un recorte que solo mirara `/` dejaría la ruta
 * entera en la cabecera.
 */
export const nombreDeRuta = (r: string): string => r.split(/[/\\]/).filter(Boolean).pop() ?? r;

/**
 * La carpeta donde vive un documento.
 *
 * El autor elige un FICHERO —es lo que sabe señalar— y de ahí se deduce la carpeta del proyecto.
 */
export const carpetaDe = (ruta: string): string => ruta.replace(/[/\\][^/\\]+$/, "");
/**
 * R-06 (17-sep): una carpeta que no puede ser la de un libro. Descargas con 614 documentos era el
 * proyecto activo de Rais: el manuscrito convivía con cientos de vecinos y el Studio proponía otros.
 * Devuelve el motivo, dicho para el autor, o null si vale.
 */
export const MAX_FICHEROS_LIBRO = 60;
export function carpetaNoValeParaLibro(ruta: string, ficheros?: number): string | null {
  const r = ruta.replace(/\\/g, "/").replace(/\/+$/, "");
  const casa = /^(\/Users\/[^/]+|\/home\/[^/]+|[A-Za-z]:\/Users\/[^/]+)$/i;
  const general = /^(\/Users\/[^/]+|\/home\/[^/]+|[A-Za-z]:\/Users\/[^/]+)\/(Downloads|Descargas|Documents|Documentos|Desktop|Escritorio|OneDrive)$/i;
  if (casa.test(r)) return "Es tu carpeta personal entera, no la de un libro.";
  const m = r.match(general);
  if (m) return `«${m[2]}» es una carpeta de todo, no la de un libro.`;
  if (ficheros != null && ficheros > MAX_FICHEROS_LIBRO) return `Tiene ${ficheros} documentos: parece una carpeta de todo, no la de un libro.`;
  return null;
}

/** ¿Está el fichero dentro de esa carpeta (o de una subcarpeta)? «/libro-2/x» no está en «/libro». */
export function dentroDeCarpeta(fichero: string, carpeta: string): boolean {
  const norm = (r: string) => r.replace(/\\/g, "/").replace(/\/+$/, "");
  const c = norm(carpeta), f = norm(fichero);
  return !!c && f.startsWith(c + "/");
}

/**
 * Qué se le enseña al sistema para abrir «la carpeta de tu libro» (2-oct-2026).
 *
 * Un probador en Windows: «¿dónde guarda lo que ha corregido? No lo encuentro». La carpeta va
 * siempre a la vista en la cabecera y se abre con un clic. El motor solo sabe SEÑALAR un fichero en
 * el Finder o el Explorador, así que se le da uno de dentro —el manuscrito, o el primero que haya—
 * y la ventana que se abre es la de la carpeta. Con la carpeta vacía se señala la carpeta misma.
 */
export function queEnsenar(carpeta: string, doc: string | null | undefined, ficheros: Pick<Fichero, "path">[]): string {
  // DIRECTAMENTE dentro, no en una subcarpeta: el motor lista tres niveles y el Studio crea
  // `capitulos/`. Señalar «capitulos/01.md» abriría esa subcarpeta mientras la franja dice otra.
  const norm = (r: string) => r.replace(/\\/g, "/").replace(/\/+$/, "");
  const suyo = (r: string) => norm(carpetaDe(r)) === norm(carpeta);
  if (doc && suyo(doc)) return doc;
  return ficheros.find((f) => suyo(f.path))?.path ?? carpeta;
}

/**
 * Qué decir cuando la carpeta no se ha podido abrir. Cuatro causas distintas no pueden leerse igual:
 * decir «está en X» de una carpeta que ya no existe es mentir.
 */
export type Mostrado = "ok" | "no_esta" | "motor_viejo" | "sin_motor";
export function avisoDeCarpeta(r: Mostrado, carpeta: string): string | null {
  if (r === "ok") return null;
  if (r === "no_esta") return `La carpeta de tu libro ya no está en ${carpeta}: puede que la hayas movido o le hayas cambiado el nombre.`;
  if (r === "motor_viejo") return `Tu motor es anterior y todavía no sabe abrir carpetas: actualízalo desde Ajustes. Tu libro está en ${carpeta}.`;
  return `No he podido abrir la carpeta porque el motor de tu ordenador no contesta. Tu libro está en ${carpeta}.`;
}

/**
 * ¿Se enseña el Studio nuevo (el flujo de cinco fases con un solo coach)?
 *
 * `?flujo=1` lo enciende y `?flujo=0` lo apaga; sin nada en la dirección, vale lo último que se
 * eligió en este navegador. Mientras se construye, quien no lo pida sigue viendo el Studio de siempre.
 */
export function flujoActivo(busqueda: string, guardado: string | null): boolean {
  const p = new URLSearchParams(busqueda).get("flujo");
  if (p === "1") return true;
  if (p === "0") return false;
  return guardado === "1";
}

/**
 * Con quién se habla en el flujo nuevo: con el coach, siempre, salvo en la entrevista del perfil de
 * autor. Esa va por su propio hilo (`author`) porque el motor escribe el perfil leyendo ESA
 * entrevista: hecha con el coach, «Escribir el perfil con la entrevista» contestaba que no había
 * ninguna y el autor había pagado los turnos para nada (equipo rojo, 3-oct).
 */
export const conQuienEnElFlujo = (id: string): string => (id === "author" ? "author" : "coach");

// ── DIAGNÓSTICO DE UNA VEZ (3-oct-2026) ─────────────────────────────────────────────────────────
// Rais: «en vez de pedir las cosas, vamos a hacerlos todos, todos los informes juntos: informe de
// lectura, el editor, el beta, el estilo y la adecuación al mercado». Cinco lecturas del libro, un
// botón. Aquí se decide en qué estado está cada una PARA LA VERSIÓN ABIERTA: un informe de la versión
// anterior no cuenta como hecho, porque habla de un texto que ya no es el tuyo.
export type TipoDiagnostico = "lectura" | "editor" | "beta" | "estilo" | "mercado";
export const PIEZAS_DIAGNOSTICO: { tipo: TipoDiagnostico; titulo: string; que: string }[] = [
  { tipo: "lectura", titulo: "Informe de lectura", que: "capítulo a capítulo, con cada problema donde está" },
  { tipo: "editor", titulo: "Informe del editor", que: "estructura, personajes, ritmo y qué cambiar primero" },
  { tipo: "beta", titulo: "Lector beta", que: "qué siente tu lector, dónde se engancha y dónde se cae" },
  { tipo: "estilo", titulo: "Estilo", que: "los diez ejes de tu voz" },
  { tipo: "mercado", titulo: "Adecuación al mercado", que: "género, comparables, palabras clave y potencial" },
];
export interface PiezaDiagnostico {
  tipo: TipoDiagnostico; titulo: string; que: string;
  /** `sin_leer`: no se ha podido saber si existe (el motor no contestó). No es «falta».
   *  `en_cola`: el diagnóstico está en marcha y este informe espera su turno (motor 1.7). */
  estado: "hecho" | "anterior" | "en_marcha" | "fallido" | "falta" | "sin_leer" | "en_cola";
  nota: number | null; resumen: string | null; de: string | null; error: string | null;
  /** Hay un informe que se puede abrir, sea o no de esta versión. */
  hay: boolean;
  /** El informe leyó solo el principio del libro (el motor lee hasta ~83.000 palabras). */
  recortado: boolean;
}
export function estadoDiagnostico(h: {
  doc: string | null;
  /** El último informe de lectura (null: no hay; undefined: no se ha podido leer). */
  informe: { doc?: string } | null | undefined;
  notaLectura: number | null;
  fichas: Record<string, { nota: number | null; resumen: string | null; version: string; recortado?: boolean }> | null | undefined;
  trabajos: { tipo: string; estado: string; error?: string | null }[];
  /** Los tipos que el motor sabe hacer; null si no lo dice (motor anterior): se ofrecen los cuatro. */
  tipos: string[] | null;
}): { piezas: PiezaDiagnostico[]; faltan: TipoDiagnostico[]; enMarcha: number; hechos: number; completo: boolean; sinLeer: boolean; enCola: number } {
  const abierto = h.doc ? nombreDeRuta(h.doc) : null;
  // M-2: con el motor nuevo, el diagnóstico es UN trabajo que pide los informes de una sola lectura de
  // uno en uno. Mientras corre, lo que no está hecho ni en marcha espera su turno: no «falta».
  const encadenado = h.trabajos.some((t) => t.tipo === "diagnostico" && t.estado === "en_curso");
  const trabajo = (tipo: TipoDiagnostico) => h.trabajos.find((t) => t.tipo === (tipo === "lectura" ? "informe-global" : "informe-" + tipo)) ?? null;
  const piezas = PIEZAS_DIAGNOSTICO
    .filter((p) => p.tipo !== "mercado" || h.tipos == null || h.tipos.includes("mercado"))
    .map((p): PiezaDiagnostico => {
      const t = trabajo(p.tipo);
      const ficha = p.tipo === "lectura" ? null : h.fichas?.[p.tipo] ?? null;
      const hay = p.tipo === "lectura" ? !!h.informe : !!ficha;
      const de = p.tipo === "lectura" ? h.informe?.doc ?? null : ficha?.version ?? null;
      const nota = p.tipo === "lectura" ? (hay ? h.notaLectura : null) : ficha?.nota ?? null;
      const base = { ...p, nota, resumen: ficha?.resumen ?? null, de, error: null as string | null, hay, recortado: !!ficha?.recortado };
      if (t?.estado === "en_curso") return { ...base, estado: "en_marcha" };
      // «No he podido leerlo» no es «no hay» (equipo rojo, 3-oct): con el motor reiniciándose, esta
      // pieza salía como sin hacer y el botón la lanzaba —y la cobraba— otra vez.
      if ((p.tipo === "lectura" ? h.informe : h.fichas) === undefined) return { ...base, estado: "sin_leer" };
      if (hay && (!de || !abierto || de === abierto)) return { ...base, estado: "hecho" };
      if (encadenado && p.tipo !== "lectura") return { ...base, estado: "en_cola" };
      if (hay) return { ...base, estado: "anterior" };
      if (t?.estado === "fallido" || t?.estado === "interrumpido") return { ...base, estado: "fallido", error: t.error ?? null };
      return { ...base, estado: "falta" };
    });
  // Con algo sin leer no se ofrece lanzar NADA: el botón es uno y lanza todo lo que falta, y aquí
  // no se sabe qué falta.
  const sinLeer = piezas.some((p) => p.estado === "sin_leer");
  const faltan = sinLeer ? [] : piezas.filter((p) => p.estado !== "hecho" && p.estado !== "en_marcha" && p.estado !== "en_cola").map((p) => p.tipo);
  const enMarcha = piezas.filter((p) => p.estado === "en_marcha").length;
  const hechos = piezas.filter((p) => p.estado === "hecho").length;
  return { piezas, faltan, enMarcha, hechos, completo: hechos === piezas.length, sinLeer, enCola: piezas.filter((p) => p.estado === "en_cola").length };
}

/**
 * El Word que junta el diagnóstico (motor 1.7), si es el de la versión abierta. El de otra versión no
 * se enseña: es el diagnóstico de un texto que ya no es el que tienes.
 */
export function wordDelDiagnostico(d: { doc?: string; ruta?: string | null; faltan?: string[] } | null | undefined, doc: string | null):
  { ruta: string; incompleto: boolean; esWord: boolean } | null {
  if (!d?.ruta || !doc || d.doc !== nombreDeRuta(doc)) return null;
  // Sin el motor de Word en el equipo, el documento sale en texto (.md): no se le llama Word.
  return { ruta: d.ruta, incompleto: (d.faltan ?? []).length > 0, esWord: /\.docx$/i.test(d.ruta) };
}

/**
 * Lo que el modelo escribió de más cuando el autor no quiere texto de la IA, y el motor no dejó entrar
 * en el Word (equipo rojo, 3-oct). Se dice siempre que pase: el autor tiene que saber que eso se miró.
 */
export function fraseProsaQuitada(p: { quitados?: number; devueltos?: number } | null | undefined): string | null {
  const q = p?.quitados ?? 0, d = p?.devueltos ?? 0;
  if (!q && !d) return null;
  const quitados = q ? `${q} ${q === 1 ? "párrafo nuevo que escribió el modelo no ha entrado" : "párrafos nuevos que escribió el modelo no han entrado"} en tu Word` : "";
  const devueltos = d ? `${d} ${d === 1 ? "párrafo al que le había añadido frases se ha quedado como estaba" : "párrafos a los que les había añadido frases se han quedado como estaban"}` : "";
  return `En tu perfil dices que no quieres texto escrito por la IA: ${[quitados, devueltos].filter(Boolean).join(" y ")}.`;
}

/**
 * Los guiones de lista que la versión ha quitado al poner rayas de diálogo (César, 4-oct: «pone las
 * rayas pero no borra los guiones»; eran viñetas de Word, no texto). En Word sale como un cambio de
 * formato: se dice aquí para que el autor sepa qué es esa marca. Un motor anterior no lo cuenta.
 */
export function fraseGuionesQuitados(n: number | null | undefined): string | null {
  if (!n || n < 1) return null;
  return n === 1
    ? "Un diálogo llevaba el guion como viñeta de lista de Word: ahora lleva su raya y la viñeta se ha quitado. En Word lo verás como un cambio de formato, que también puedes rechazar."
    : `${n} diálogos llevaban el guion como viñeta de lista de Word: ahora llevan su raya y la viñeta se ha quitado. En Word lo verás como cambios de formato, que también puedes rechazar.`;
}

/** Los sitios del manuscrito donde al autor le toca escribir (las indicaciones que siguen ahí). */
export function avisoDeIndicaciones(n: number | null | undefined): string | null {
  if (!n || n < 1) return null;
  return `tiene ${n} ${n === 1 ? "sitio donde te toca escribir a ti" : "sitios donde te toca escribir a ti"}: busca «[ESCRIBE AQUÍ» en Word, escribe tu texto en su lugar y guarda.`;
}

/**
 * Lo que el motor añade a cada informe además del libro: su oficio (2.000–6.000 tokens) y el contexto
 * del proyecto (hasta 60.000 caracteres). Sin contarlo, el total del diagnóstico se quedaba entre un
 * 5 y un 20 % corto (equipo rojo, 3-oct). Es una cifra media: mejor pasarse un poco que quedarse corto.
 */
export const TOKENS_ANADIDOS_POR_INFORME = 12000;

/**
 * La lectura más cara que se ha hecho en esta carpeta. No «la última»: una lectura reanudada solo
 * paga lo que faltaba, y decir esa cifra como lo que cuesta leer el libro sería quedarse muy corto.
 */
export function lecturaMasCara(operaciones: { op: string; coste: number }[]): number | null {
  const costes = operaciones.filter((o) => o.op === "informe-global" && o.coste > 0).map((o) => o.coste);
  return costes.length ? Math.max(...costes) : null;
}

/**
 * Lo que va a costar lo que falta del diagnóstico, dicho antes de gastar. Los cuatro informes de
 * libro entero se calculan (palabras × precio); el de lectura va capítulo a capítulo y no tiene
 * cuenta que valga: se dice lo que ha llegado a costar en esta carpeta, o que no se sabe.
 */
export function costeDelDiagnostico(
  faltan: TipoDiagnostico[], palabras: number | null,
  precios: Record<string, { entrada: number | null; salida: number | null } | undefined>,
  lecturaMedida: number | null,
): { calculado: number | null; lectura: "no_toca" | "medida" | "sin_medir"; lecturaMedida: number | null } {
  const AGENTE: Record<string, string> = { editor: "editor", beta: "coach", estilo: "coach", mercado: "coach" };
  const cortos = faltan.filter((t) => t !== "lectura");
  let calculado: number | null = 0;
  for (const t of cortos) {
    const precio = precios[AGENTE[t]];
    const c = palabras == null ? null : costeInforme(palabras, precio);
    if (c == null) { calculado = null; break; }
    calculado += c + (TOKENS_ANADIDOS_POR_INFORME / 1e6) * (precio!.entrada ?? 0);
  }
  if (!cortos.length) calculado = null;
  const toca = faltan.includes("lectura");
  return { calculado, lectura: !toca ? "no_toca" : lecturaMedida != null && lecturaMedida > 0 ? "medida" : "sin_medir", lecturaMedida: toca ? lecturaMedida : null };
}

/**
 * El plan, en cifras para aprobarlo de un vistazo: cuántas fases, cuántas pasadas se van a ejecutar,
 * cuántas ha quitado el autor y cuántas están ya hechas. Una fase sin pasadas no cuenta.
 */
export function resumenDelPlan(plan: { areas?: { pasadas?: { estado?: string }[] }[] } | null | undefined):
  { fases: number; total: number; pendientes: number; fuera: number; hechas: number } {
  const areas = (plan?.areas ?? []).filter((a) => (a.pasadas ?? []).length > 0);
  const todas = areas.flatMap((a) => a.pasadas ?? []);
  const hechas = todas.filter((p) => p.estado === "hecha").length;
  const fuera = todas.filter((p) => p.estado === "saltada").length;
  return { fases: areas.length, total: todas.length, pendientes: todas.length - hechas - fuera, fuera, hechas };
}

// ── LO QUE EL AUTOR DEJA HACER CON SU TEXTO (M-1, 3-oct-2026) ───────────────────────────────────
// Rais: el perfil del autor recoge si acepta poda, cambio de estilo y texto nuevo de la IA, y lo que no
// se toca. Se dice una vez, en Autores, y el motor lo aplica en el plan, en cada pasada y en la
// corrección. Aquí, solo cómo se pregunta y cómo se cuenta.
export interface PreferenciasEdicion {
  poda: boolean; estilo: boolean; texto_ia: boolean; intocables: string[];
  /** false: el autor todavía no ha dicho nada, y se acepta todo como hasta hoy. */
  declaradas: boolean; fecha: string | null;
  /** La firma del libro, cuando se piden por libro; null si el libro no tiene firma. */
  autor?: { id: string; nombre: string } | null;
}
export type PermisoDeEdicion = "poda" | "estilo" | "texto_ia";
export const PREGUNTAS_DE_EDICION: { id: PermisoDeEdicion; titulo: string; si: string; no: string }[] = [
  { id: "poda", titulo: "Poda",
    si: "Acepto que se quite texto: escenas, párrafos o frases que sobran. Lo decido yo en Word, cambio a cambio.",
    no: "No quiero que se quite nada. Las pasadas de poda no se hacen." },
  { id: "estilo", titulo: "Cambio de estilo",
    si: "Acepto que se toque mi estilo: la frase, el ritmo, los adjetivos.",
    no: "Mi estilo no se toca. En la corrección, solo errores." },
  { id: "texto_ia", titulo: "Texto nuevo escrito por la IA",
    si: "Acepto que la IA escriba texto nuevo donde haga falta. Lo acepto o lo rechazo en Word.",
    no: "No quiero texto escrito por la IA. Donde haga falta, me deja dicho qué escribir y lo escribo yo." },
];
const lineasDe = (t: string) => [...new Set(t.split("\n").map((x) => x.replace(/\s+/g, " ").trim()).filter(Boolean))];

/** Las preferencias, en una línea. `null` si el motor no las tiene (anterior a la 1.7). */
export function resumenDePreferencias(p: PreferenciasEdicion | null | undefined): string | null {
  if (!p) return null;
  if (!p.declaradas) return "Todavía no has dicho qué aceptas: hoy se acepta todo (poda, cambios de estilo y texto nuevo de la IA).";
  const no = [
    p.poda ? null : "sin poda",
    p.estilo ? null : "tu estilo no se toca",
    p.texto_ia ? null : "el texto nuevo lo escribes tú",
    p.intocables.length ? `${p.intocables.length} ${p.intocables.length === 1 ? "cosa que no se toca" : "cosas que no se tocan"}` : null,
  ].filter(Boolean);
  return no.length ? no.join(" · ") : "aceptas poda, cambios de estilo y texto nuevo de la IA";
}

/**
 * Lo que hay que guardar: solo lo que ha cambiado. `null` si no ha cambiado nada. La primera vez
 * (sin declarar) se guarda todo aunque sea «lo acepto todo»: es declararlo.
 */
export function cambiosDePreferencias(antes: PreferenciasEdicion,
  ahora: { poda: boolean; estilo: boolean; texto_ia: boolean; intocables: string }):
  Partial<{ poda: boolean; estilo: boolean; texto_ia: boolean; intocables: string[] }> | null {
  const zonas = lineasDe(ahora.intocables);
  if (!antes.declaradas) return { poda: ahora.poda, estilo: ahora.estilo, texto_ia: ahora.texto_ia, intocables: zonas };
  const c: Partial<{ poda: boolean; estilo: boolean; texto_ia: boolean; intocables: string[] }> = {};
  for (const k of ["poda", "estilo", "texto_ia"] as const) if (ahora[k] !== antes[k]) c[k] = ahora[k];
  if (zonas.join("\n") !== antes.intocables.join("\n")) c.intocables = zonas;
  return Object.keys(c).length ? c : null;
}

/**
 * Lo que se dice junto a una pasada del plan cuando el perfil del autor la toca: no se puede marcar
 * (va contra lo que ha dicho) o se hará dejando indicaciones en vez de texto. El motor es quien lo
 * decide; aquí solo se cuenta.
 */
export function notaDePasada(p: { estado?: string; contra?: string[]; modo?: string }): { bloqueada: boolean; texto: string | null } {
  if (p.estado === "hecha") return { bloqueada: false, texto: null };
  if (p.contra?.length) {
    const que = p.contra.map((k) => (k === "poda" ? "poda" : k === "estilo" ? "cambios de estilo" : k)).join(" ni ");
    return { bloqueada: true, texto: `en tu perfil dices que no quieres ${que}; si has cambiado de idea, cámbialo en Autores` };
  }
  if (p.modo === "indicacion") return { bloqueada: false, texto: "no escribe texto nuevo: donde haga falta te deja dicho qué escribir, y lo escribes tú" };
  return { bloqueada: false, texto: null };
}

// ── PUBLICACIÓN EN DOS CAMINOS (3-oct-2026) ─────────────────────────────────────────────────────
// Rais: «generar dos opciones. Una, tradicional, y la otra, independiente. En la tradicional, de
// golpe, te crea carta, briefing y Excel para las editoriales y agentes. Y la de independiente te crea
// la maquetación EPUB y PDF, la ficha de Amazon, los prompts de las portadas y, cuando las tenga, el
// wrap». Cada camino es una lista de encargos que el motor ya sabe hacer; aquí se decide cuáles se
// pueden lanzar de golpe y cuáles no (porque están hechos, en marcha, o piden un dato que solo sabe
// el autor). El Excel llega con el motor nuevo: mientras, la lista de editoriales sale en Word.
export const CAMINOS_DE_PUBLICACION = {
  tradicional: {
    titulo: "Editorial tradicional",
    que: "La carta de presentación, el dossier para el editor y la lista de editoriales que encajan con tu libro.",
    ids: ["query", "briefing", "editoriales"],
  },
  independiente: {
    titulo: "Publicar por tu cuenta",
    que: "La ficha de Amazon, tres ideas de portada con sus prompts y los textos de contraportada. El libro maquetado y la cubierta van debajo.",
    ids: ["ficha-amazon", "portada-conceptos", "contraportada"],
  },
} as const;
export type CaminoDePublicacion = keyof typeof CAMINOS_DE_PUBLICACION;

export interface PiezaDeLote {
  id: string; titulo: string;
  estado: "hecho" | "en_marcha" | "falta" | "pide" | "fallido" | "no_disponible";
  /** Lo que el autor tiene que decir antes (un dato obligatorio que el motor no sabe proponer). */
  pide: string | null;
  ruta: string | null; error: string | null;
  /** Los datos con que se lanzaría: lo que el motor propone para cada campo. */
  datos: Record<string, string>;
  caracteres: number; agente: string;
  /** La hoja de Excel que dejó junto al Word (la de seguimiento de editoriales, motor 1.7), si la hay. */
  excel: string | null;
  /** El motor sabe hacer esa hoja para este encargo. Con uno anterior, la lista sale solo en Word. */
  conExcel: boolean;
}
export function loteDeEncargos(
  ids: readonly string[],
  catalogo: { id: string; titulo: string; agente: string; caracteres: number; excel?: boolean;
              pide: { campo: string; etiqueta: string; obligatorio: boolean; valor?: string }[];
              hecho: { ruta: string; excel?: string } | null }[],
  trabajos: { tipo: string; estado: string; error?: string | null; resultado?: Record<string, unknown> | null }[],
): { piezas: PiezaDeLote[]; lanzables: PiezaDeLote[]; hechos: number; enMarcha: number } {
  const piezas = ids.map((id): PiezaDeLote => {
    const e = catalogo.find((x) => x.id === id);
    if (!e) return { id, titulo: id, estado: "no_disponible", pide: null, ruta: null, error: null, datos: {}, caracteres: 0, agente: "", excel: null, conExcel: false };
    const t = trabajos.find((x) => x.tipo === "encargo-" + id) ?? null;
    const datos = Object.fromEntries(e.pide.map((p) => [p.campo, p.valor ?? ""]));
    const sinDato = e.pide.find((p) => p.obligatorio && !(p.valor ?? "").trim());
    const base = { id, titulo: e.titulo, pide: null as string | null, ruta: e.hecho?.ruta ?? null, error: null as string | null, datos, caracteres: e.caracteres, agente: e.agente,
      excel: e.hecho?.excel ?? null, conExcel: !!e.excel };
    if (t?.estado === "en_curso") return { ...base, estado: "en_marcha" };
    if (e.hecho) return { ...base, estado: "hecho" };
    if (t?.estado === "hecho" && typeof t.resultado?.ruta === "string") return { ...base, estado: "hecho", ruta: t.resultado.ruta, excel: typeof t.resultado.excel === "string" ? t.resultado.excel : base.excel };
    if (sinDato) return { ...base, estado: "pide", pide: sinDato.etiqueta.replace(/[¿?]/g, "").trim() };
    if (t?.estado === "fallido" || t?.estado === "interrumpido") return { ...base, estado: "fallido", error: t.error ?? null };
    return { ...base, estado: "falta" };
  });
  return {
    piezas,
    lanzables: piezas.filter((p) => p.estado === "falta" || p.estado === "fallido"),
    hechos: piezas.filter((p) => p.estado === "hecho").length,
    enMarcha: piezas.filter((p) => p.estado === "en_marcha").length,
  };
}

/** Lo que cuesta lanzar un lote; null si de alguno no se sabe el precio (no se da media cifra). */
export function costeDelLote(lanzables: Pick<PiezaDeLote, "caracteres" | "agente">[],
  precios: Record<string, { entrada: number | null; salida: number | null } | undefined>): number | null {
  if (!lanzables.length) return null;
  let total = 0;
  for (const p of lanzables) {
    const c = costeEncargo(p.caracteres, precios[p.agente]);
    if (c == null) return null;
    total += c;
  }
  return total;
}

// ── CONTROL: las cifras del texto y cómo cambian de una versión a otra (3-oct-2026) ──────────────
// El motor mide cada versión al escribirla (las mismas cuentas para todas), y hasta hoy esas cifras
// llegaban al Studio y nadie las pintaba. Aquí se decide cuáles se enseñan, con qué nombre y con qué
// frase: una cifra sin decir qué mide no le sirve a un autor.
export interface Medida { id: string; nombre: string; que: string; unidad?: string; decimales: number }
export const MEDIDAS: Medida[] = [
  { id: "palabras", nombre: "Palabras", que: "lo que mide tu libro", decimales: 0 },
  { id: "frases", nombre: "Frases", que: "cuántas frases tiene", decimales: 0 },
  { id: "frase_media", nombre: "Frase media", que: "palabras por frase, de media", unidad: "palabras", decimales: 1 },
  { id: "frase_mediana", nombre: "Frase típica", que: "la mitad de tus frases son más cortas que esto", unidad: "palabras", decimales: 0 },
  { id: "frase_sigma", nombre: "Variedad de frase", que: "cuánto se apartan tus frases de la media: más alto, más variedad", decimales: 1 },
  { id: "alternancia", nombre: "Alternancia", que: "cuánto cambia el largo de una frase a la siguiente: el ritmo", decimales: 1 },
  { id: "frases_cortas_pct", nombre: "Frases cortas", que: "frases de ocho palabras o menos", unidad: "%", decimales: 0 },
  { id: "parrafo_mediana", nombre: "Párrafo típico", que: "la mitad de tus párrafos son más cortos que esto", unidad: "palabras", decimales: 0 },
  { id: "parrafo_p90", nombre: "Párrafos largos", que: "uno de cada diez párrafos pasa de esto", unidad: "palabras", decimales: 0 },
  { id: "dialogo_pct_lineas", nombre: "Diálogo", que: "párrafos que son diálogo", unidad: "%", decimales: 0 },
  { id: "adverbios_mente_por_mil", nombre: "Adverbios en -mente", que: "por cada mil palabras", decimales: 1 },
  { id: "cortes_de_escena", nombre: "Cortes de escena", que: "separadores entre escenas", decimales: 0 },
];

type VersionMedida = { n: number; build: string | null; palabras: number; metricas: Record<string, number> | null };

/** El valor de una medida en una versión; null si esa versión no se ha medido. «Palabras» va aparte. */
export function valorDeMedida(v: VersionMedida, id: string): number | null {
  if (id === "palabras") return Number.isFinite(v.palabras) && v.palabras > 0 ? v.palabras : null;
  const x = v.metricas?.[id];
  return typeof x === "number" && Number.isFinite(x) ? x : null;
}

/** Una cifra, escrita como se escribe en España: 45.848 · 13,2. */
export function cifra(x: number | null, decimales = 0): string {
  if (x == null) return "—";
  // A mano: el formato del sistema no pone el punto de millar a un número de cuatro cifras («4200»).
  const [ent, dec] = Math.abs(x).toFixed(decimales).split(".");
  return (x < 0 ? "−" : "") + ent.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + (dec ? "," + dec : "");
}

/**
 * La tabla de Control: una fila por medida y una columna por versión (las últimas `max`, de la más
 * vieja a la más nueva), con el cambio entre la primera y la última de las que se han medido. Una
 * medida que ninguna versión trae no sale: una fila de rayas no dice nada.
 */
export function comparativa(versiones: VersionMedida[], max = 6): {
  columnas: { n: number; build: string | null }[];
  filas: { medida: Medida; valores: (number | null)[]; cambio: number | null }[];
} {
  const cols = [...versiones].sort((a, b) => a.n - b.n).slice(-max);
  const filas = MEDIDAS.map((medida) => {
    const valores = cols.map((v) => valorDeMedida(v, medida.id));
    const medidos = valores.filter((x): x is number => x != null);
    const cambio = medidos.length >= 2 ? medidos[medidos.length - 1] - medidos[0] : null;
    return { medida, valores, cambio };
  }).filter((f) => f.valores.some((x) => x != null));
  return { columnas: cols.map((v) => ({ n: v.n, build: v.build })), filas };
}

/**
 * LO QUE HAS HECHO CON LOS CAMBIOS, versión a versión (M-9, motor 1.7). Sale del manifiesto de cada
 * versión, leído de vuelta de Word. Solo las versiones que lo traen: al original nadie le propuso
 * nada, y un motor anterior no lo cuenta.
 */
export interface DecisionDeVersion { propuestos: number; aceptados: number; retocados: number; rechazados: number; suyos: number; no_evaluables: number; sin_decidir: number; indicaciones?: number }
export const FILAS_DE_DECISION: { id: keyof DecisionDeVersion; nombre: string; que: string }[] = [
  { id: "propuestos", nombre: "Cambios propuestos", que: "los que llevaba esa versión cuando te llegó" },
  { id: "aceptados", nombre: "Aceptados", que: "los que dejaste tal cual en Word" },
  { id: "retocados", nombre: "Aceptados con retoques", que: "los que aceptaste cambiando algo" },
  { id: "rechazados", nombre: "Rechazados", que: "los que devolviste a como estaban" },
  { id: "suyos", nombre: "Texto tuyo en su lugar", que: "donde quitaste lo propuesto y escribiste lo tuyo" },
  { id: "indicaciones", nombre: "Sitios donde te toca escribir", que: "indicaciones «[ESCRIBE AQUÍ…]» que siguen en el Word sin tu texto" },
  { id: "no_evaluables", nombre: "No se ha podido saber", que: "cambios demasiado pequeños para seguirles la pista" },
  { id: "sin_decidir", nombre: "Sin decidir todavía", que: "siguen marcados en Word, o aún no se ha leído el documento" },
];
export function decisionesPorVersion(versiones: { n: number; build: string | null; decision?: DecisionDeVersion | null }[], max = 6): {
  columnas: { n: number; build: string | null }[];
  filas: { id: keyof DecisionDeVersion; nombre: string; que: string; valores: number[] }[];
  /** Por versión, el % de lo decidido que se quedó (aceptado, con o sin retoques). `null` sin nada decidido. */
  aceptacion: (number | null)[];
} {
  const cols = versiones.filter((v) => v.decision).sort((a, b) => a.n - b.n).slice(-max);
  const filas = FILAS_DE_DECISION
    .map((f) => ({ ...f, valores: cols.map((v) => v.decision![f.id] ?? 0) }))
    .filter((f) => f.valores.some((x) => x > 0));
  const aceptacion = cols.map((v) => {
    const d = v.decision!;
    const decididos = d.aceptados + d.retocados + d.rechazados + d.suyos;
    return decididos > 0 ? Math.round(((d.aceptados + d.retocados) / decididos) * 100) : null;
  });
  return { columnas: cols.map((v) => ({ n: v.n, build: v.build })), filas, aceptacion };
}

/** El cambio, con su signo y sin presumir de lo que no se mueve: «+3,2», «−412», «igual». */
export function cambioEnPalabras(cambio: number | null, decimales = 0): string {
  if (cambio == null) return "";
  const redondo = Number(cambio.toFixed(decimales));
  if (redondo === 0) return "igual";
  return (redondo > 0 ? "+" : "−") + cifra(Math.abs(redondo), decimales);
}

/**
 * La versión del motor que le toca a quien mira, y su instalador.
 *
 * Mac y Windows pueden ir a distinta versión, en los dos sentidos: Windows se quedó atrás en
 * septiembre, y la 1.6.33 salió antes en Windows porque arreglaba un fallo que solo era suyo. El
 * aviso «Actualiza a la X» comparaba siempre con la de Mac: a quien estaba en Windows le decía que
 * estaba al día cuando no lo estaba. Sin saber el sistema, la más alta no se adivina: la de Mac.
 */
export function versionDeTuSistema(
  m: { version: string; winVersion?: string; pkg?: string; win?: string; publicado?: string; winPublicado?: string },
  sist: "mac" | "win" | null,
): { version: string; url: string | null; publicado: string | null } {
  if (sist === "win") return { version: m.winVersion ?? m.version, url: m.win ?? null, publicado: m.winPublicado ?? m.publicado ?? null };
  return { version: m.version, url: m.pkg ?? null, publicado: m.publicado ?? null };
}

/**
 * Los documentos, con el que el autor señaló el primero.
 *
 * Sin esto, en una carpeta como Descargas el suyo aparecía enterrado entre 531 vecinos en orden
 * alfabético, tanto en la lista del manuscrito como en las pastillas para adjuntar del chat.
 */
export function ordenarPorElegido(ficheros: Fichero[], doc?: string | null): Fichero[] {
  if (!doc) return ficheros;
  return [...ficheros].sort((a, b) => (a.path === doc ? -1 : b.path === doc ? 1 : 0));
}

/**
 * Qué manuscrito propone una pantalla que va a gastar dinero.
 *
 * MANDA LO QUE EL AUTOR SEÑALÓ. Adivinar por tamaño solo vale si no señaló nada: en una carpeta con
 * muchos documentos el más grande no es su libro, y aquí la adivinanza no es un detalle estético,
 * porque el botón de Analizar gasta su clave de OpenRouter con lo que se le diga.
 */
export function manuscritoPropuesto(ficheros: Fichero[], doc?: string | null): Fichero | undefined {
  const señalado = doc ? ficheros.find((f) => f.path === doc) : undefined;
  return señalado ?? [...ficheros].sort((a, b) => b.size - a.size)[0];
}

/**
 * DÓNDE CAE EL LIBRO EN EL PLANO de AWAP 2: la geometría, aparte del dibujo.
 *
 * Devuelve la posición en un cuadrado de 0 a 100 en los dos ejes, y la banda como tramo, porque la
 * norma (§5) obliga a enseñar el punto CON su banda y no la cifra sola. Sin puntuación no hay
 * punto: un libro sin proceso registrado no está en ninguna altura del eje Y, y dibujarlo en el
 * suelo sería afirmar que su autoría observada es cero cuando lo que pasa es que no se observó.
 */
export interface PuntoPlano {
  x: number;                                  // cobertura, 0-100
  y: number | null;                           // puntuación exacta, o null si no hay
  banda: { desde: number; hasta: number } | null;
  cuadrante: "Q1" | "Q2" | "Q3" | "Q4";
}

const acotar = (n: number) => Math.max(0, Math.min(100, n));

export function puntoDelPlano(cobertura: number, valor: number | null, banda?: string | null): PuntoPlano {
  const x = acotar(Number(cobertura) || 0);
  const y = typeof valor === "number" && Number.isFinite(valor) ? acotar(valor) : null;
  // «60-69» → el tramo. Una banda con otra forma se ignora en vez de dibujar un rectángulo al azar.
  const m = /^(\d{1,3})-(\d{1,3})$/.exec(String(banda ?? ""));
  const tramo = m ? { desde: acotar(Number(m[1])), hasta: acotar(Number(m[2])) } : null;
  const cuadrante = y == null ? "Q3" : x >= 50 ? (y >= 50 ? "Q1" : "Q4") : (y >= 50 ? "Q2" : "Q3");
  return { x, y, banda: tramo && y != null ? tramo : null, cuadrante };
}

/** Lo que devuelve el motor al mirar una carpeta. `existe` y `carpeta` son campos añadidos. */
export interface Miradaproyecto {
  // `unknown[]` a propósito: de la lista solo hace falta CUÁNTOS hay. Tipar aquí la forma de un
  // Fichero ataría esta decisión al contrato del motor sin ninguna ganancia.
  files: unknown[];
  existe?: boolean;
  carpeta?: boolean;
}

/**
 * Qué decirle al autor sobre la carpeta que ha elegido, o `null` si está bien.
 *
 * TRES COSAS DISTINTAS QUE SE CONTABAN IGUAL. El motor devolvía 200 y cero ficheros tanto si la
 * carpeta estaba vacía como si ya no existía —borrada, renombrada, o en un disco desenchufado— y el
 * panel decía «ahí no hay ningún documento», que manda a buscar el fallo donde no está.
 *
 * `existe` es opcional a propósito: un panel nuevo contra un motor viejo lo recibe `undefined` y
 * entonces NO se afirma nada, en vez de dar un falso positivo.
 */
export function problemaDeCarpeta(r: Miradaproyecto): string | null {
  if (r.existe === false) {
    return "Esa carpeta ya no está en tu ordenador. ¿La has movido, o venía de un disco que ahora no está conectado?";
  }
  if (r.existe === true && r.carpeta === false) {
    return "Eso es un fichero suelto, no una carpeta. Elige el manuscrito y nosotros deducimos dónde vive.";
  }
  if (!r.files.length) {
    return "Esa carpeta existe, pero no tiene ningún documento dentro. ¿Es la del manuscrito?";
  }
  return null;
}

/**
 * IDEMPOTENCY-KEY (Acciones, 13-sep-2026): una huella del CONTENIDO del encargo, no un azar.
 *
 * El riesgo real no es el doble clic en la misma pestaña —el botón ya se deshabilita mientras
 * espera—: es recargar la página justo después de pulsar y volver a pulsar creyendo que no pasó
 * nada, o dos pestañas del mismo proyecto abiertas. En los dos casos el segundo intento es un
 * fetch() nuevo, sin memoria del primero, así que una clave aleatoria generada en cada llamada no
 * protegería nada: sería distinta cada vez. Una clave que depende del CONTENIDO sí sobrevive a
 * eso, porque el segundo intento manda el mismo encargo con la misma clave.
 *
 * El precio de esto, dicho una vez: si el autor pide dos veces EL MISMO encargo a propósito en
 * menos de diez minutos, la segunda le devuelve la respuesta de la primera en vez de regenerar.
 * Es un caso raro —la pantalla no invita a repetir exactamente lo mismo— y se resuelve cambiando
 * una palabra del encargo o esperando.
 */
export async function claveIdempotencia(cuerpo: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(cuerpo));
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * ESTIMACIÓN DE COSTE ANTES DE GASTAR (Acciones, DECISIONES.md §5).
 *
 * «Antes de un encargo largo, una línea: "esto son ~4 minutos y unos 0,50 $ de tu clave" y un
 * botón. Sin diálogo modal, sin fricción, pero dicho antes.» Esto es esa línea, calculada aquí
 * porque el Studio ya tiene todo lo que hace falta sin pedirle nada nuevo al motor: el catálogo de
 * OpenRouter trae el precio real por millón de tokens (`ModeloCatalogo`), y `/telemetry` ya trae,
 * por colaborador, cuántas llamadas ha hecho y cuánto han tardado en total.
 *
 * DOS CIFRAS DE NATURALEZA DISTINTA, y por eso el resultado lo dice (`fuente`):
 *  - el DINERO es una cuenta: tokens de entrada y salida × la tarifa real del modelo. No hay forma
 *    de saber cuánto va a escribir el modelo antes de que lo haga, así que la salida se estima como
 *    una fracción de la entrada (`RATIO_SALIDA`) — es una aproximación y se llama así en el nombre
 *    de la constante, no una medición.
 *  - la DURACIÓN, cuando ya se ha hablado antes con ese colaborador, es la media de sus llamadas
 *    reales (`historial.ms / historial.calls`) — medida, no adivinada. Solo sin ningún historial
 *    cae a una suposición conservadora de tokens por segundo.
 *
 * `TOKENS_POR_PALABRA = 1.5` es una aproximación documentada para español con tokenizadores BPE
 * (más alto que el ~1.3 habitual en inglés, por los acentos y la mayor longitud media de palabra).
 * No pretende ser exacta: pretende que la cifra que se enseña sea del orden correcto, que es lo que
 * pide la decisión — «~», no un céntimo exacto.
 */
/** Cuántas palabras tiene un texto — el mismo criterio que `words()` en el motor (chapters.mjs). */
export const palabrasDe = (texto: string): number => {
  const t = texto.trim();
  return t ? t.split(/\s+/).length : 0;
};

const TOKENS_POR_PALABRA = 1.5;
const RATIO_SALIDA = 0.6;          // una respuesta de reescritura/corrección, del orden de la entrada
const SALIDA_MIN_TOKENS = 300;
const SALIDA_MAX_TOKENS = 4000;
const TOKENS_POR_SEGUNDO_SIN_HISTORIAL = 20;   // suposición conservadora: sin ninguna llamada previa
// LA REESCRITURA, MEDIDA (26-sep, 90 llamadas reales de corrección con Sonnet 5 y el esfuerzo por
// defecto): cada llamada lleva ~13.000 tokens de oficio además del texto, y devuelve el texto más
// unas 6 veces su tamaño en razonamiento (se cobra como salida). La estimación de antes se quedaba
// ~5 veces corta: «~0,03 $» para un capítulo que costó 0,23 $.
const SISTEMA_REESCRITURA_TOKENS = 13000;      // por llamada
const PALABRAS_POR_LLAMADA = 1600;             // lo que trocea la corrección
const RAZONAMIENTO_POR_TOKEN = 6;              // razonamiento por cada token de texto devuelto
const TOKENS_POR_SEGUNDO_REESCRITURA = 90;     // medido: 16.800 tokens en ~170 s
// Cada modelo razona distinto: con el mismo capítulo de DMVM (26-sep, capítulo 10, 1.849 palabras),
// Sonnet 5 devolvió 22.976 tokens (18.517 razonando) en 251 s y Opus 5.5, 8.609 (3.716 razonando)
// en 82 s. Con el factor de Sonnet, la estimación de Opus salía casi el triple de lo real.
// Opus 5.5: UNA medición; se corrige cuando haya correcciones reales en la telemetría.
const RITMOS: { patron: RegExp; razonamiento: number; tokensPorSegundo: number }[] = [
  { patron: /claude-opus-5\.5/, razonamiento: 2, tokensPorSegundo: 105 },
];
const ritmoDe = (id?: string) => RITMOS.find((r) => id && r.patron.test(id))
  ?? { razonamiento: RAZONAMIENTO_POR_TOKEN, tokensPorSegundo: TOKENS_POR_SEGUNDO_REESCRITURA };

export interface EstimacionCoste {
  palabras: number;
  tokensEntrada: number;
  tokensSalida: number;
  coste: number | null;          // USD; null si el modelo no trae precio en el catálogo
  minutos: number;
  fuente: "medida" | "estimada"; // si los minutos vienen de llamadas reales a este colaborador
}

export function estimarCoste(
  palabras: number,
  modelo: { id?: string; entrada: number | null; salida: number | null } | undefined,
  historial?: { calls: number; ms: number },
  // R-05 (17-sep): una REESCRITURA devuelve un texto del tamaño del que recibe. Con el tope de
  // 4.000 tokens de salida, reescribir un libro salía «~1 min y ~0,04 $». Y su duración no se
  // saca del historial, que son encargos cortos: se calcula por tokens.
  modo: "normal" | "reescritura" = "normal",
  // Un capítulo suelto (chat del corrector, «Corregir → un capítulo») va en UNA llamada: el oficio
  // viaja una vez. Solo la corrección y las pasadas del libro entero trocean (27-sep: el capítulo 17
  // de DMVM costó 0,19 $ y la pantalla, que lo partía en dos, decía ~0,27 $).
  { unaLlamada = false }: { unaLlamada?: boolean } = {},
): EstimacionCoste {
  const p = Math.max(0, Math.round(palabras) || 0);
  const tokensTexto = Math.round(p * TOKENS_POR_PALABRA);
  const llamadas = unaLlamada ? 1 : Math.max(1, Math.ceil(p / PALABRAS_POR_LLAMADA));
  const tokensEntrada = modo === "reescritura" ? tokensTexto + llamadas * SISTEMA_REESCRITURA_TOKENS : tokensTexto;
  const ritmo = ritmoDe(modelo?.id);
  const tokensSalida = modo === "reescritura"
    ? Math.max(SALIDA_MIN_TOKENS, Math.round(tokensTexto * (1 + ritmo.razonamiento)))
    : Math.min(SALIDA_MAX_TOKENS, Math.max(SALIDA_MIN_TOKENS, Math.round(tokensTexto * RATIO_SALIDA)));
  if (modo === "reescritura") historial = undefined;
  const coste = modelo && modelo.entrada != null && modelo.salida != null
    ? (tokensEntrada / 1e6) * modelo.entrada + (tokensSalida / 1e6) * modelo.salida
    : null;
  const medida = !!historial && historial.calls > 0;
  const minutos = medida
    ? (historial!.ms / historial!.calls) / 60_000
    : tokensSalida / (modo === "reescritura" ? ritmo.tokensPorSegundo : TOKENS_POR_SEGUNDO_SIN_HISTORIAL) / 60;
  return { palabras: p, tokensEntrada, tokensSalida, coste, minutos, fuente: medida ? "medida" : "estimada" };
}

/**
 * Qué documentos de la carpeta del proyecto se enseñan. Con Descargas como proyecto salían 600
 * pastillas (14-sep-2026). La regla es la del compositor del chat: el elegido y el abierto, y el
 * resto se busca por nombre, ocho como mucho. La carpeta de un libro de verdad se enseña entera.
 */
export const POCOS_DOCUMENTOS = 12;
export const MUCHOS_DOCUMENTOS = 60;

export function documentosVisibles(
  ficheros: Fichero[],
  { doc, abierto, filtro = "", max = 8 }: { doc?: string | null; abierto?: string | null; filtro?: string; max?: number } = {},
) {
  const total = ficheros.length;
  const recortada = total > POCOS_DOCUMENTOS;
  const q = filtro.trim().toLowerCase();
  const fijos = recortada ? ficheros.filter((f) => f.path === doc || f.path === abierto) : ficheros;
  const buscados = recortada && q
    ? ficheros.filter((f) => f.path !== doc && f.path !== abierto && f.name.toLowerCase().includes(q)).slice(0, max)
    : [];
  return { fijos: ordenarPorElegido(fijos, doc), buscados, total, recortada, muchos: total > MUCHOS_DOCUMENTOS };
}

/**
 * Por dónde va un mensaje del chat. Solo los colaboradores que REESCRIBEN van por la reescritura
 * (`/chat`: operación en la bitácora y «Aplicar al manuscrito»); los demás conversan y contestan en
 * el chat. Hasta el 15-sep cualquier colaborador de tipo documento convertía cada mensaje en un Word.
 */
export type CaminoDelChat = "reescritura" | "conversacion";
export const caminoDelChat = (output: string): CaminoDelChat => (output === "rewrite" ? "reescritura" : "conversacion");

/** `buildContext` del motor corta cada adjunto a 8.000 caracteres: unas 1.450 palabras en español. */
export const CARACTERES_POR_ADJUNTO = 8000;
export const PALABRAS_MAX_POR_ADJUNTO = 1450;

/** Las palabras que de verdad salen hacia el modelo si se pulsa enviar ahora (para estimar el coste). */
export function palabrasQueViajan({ camino, encargo, abiertoTexto, abiertoRuta, capituloTexto, adjuntos, libroEntero = false }: {
  camino: CaminoDelChat; encargo: string; abiertoTexto?: string; abiertoRuta?: string; capituloTexto?: string; adjuntos: string[];
  libroEntero?: boolean;   // R-18: el documento abierto viaja completo, no recortado
}): number {
  if (camino === "reescritura") {
    return palabrasDe(capituloTexto ?? abiertoTexto ?? encargo) + adjuntos.length * PALABRAS_MAX_POR_ADJUNTO;
  }
  const ficheros = new Set(adjuntos);
  if (abiertoRuta) ficheros.add(abiertoRuta);
  const capitulo = capituloTexto ? palabrasDe(capituloTexto.slice(0, CARACTERES_POR_ADJUNTO)) : 0;
  if (libroEntero && abiertoRuta && abiertoTexto != null) {
    ficheros.delete(abiertoRuta);
    return palabrasDe(encargo) + capitulo + palabrasDe(abiertoTexto) + ficheros.size * PALABRAS_MAX_POR_ADJUNTO;
  }
  return palabrasDe(encargo) + capitulo + ficheros.size * PALABRAS_MAX_POR_ADJUNTO;
}

/**
 * Qué se recorta al conversar (R-05): el motor corta cada adjunto a 8.000 caracteres. Del documento
 * abierto se sabe exacto; del resto de adjuntos no (el tamaño del .docx no es su texto), así que se
 * dicen todos como «hasta 1.450 palabras». En la reescritura viaja entero el texto de trabajo.
 */
export function recortesAlConversar({ camino, abiertoTexto, abiertoRuta, adjuntos, libroEntero = false }: {
  camino: CaminoDelChat; abiertoTexto?: string; abiertoRuta?: string; adjuntos: string[]; libroEntero?: boolean;
}): { ruta: string; seguro: boolean }[] {
  if (camino === "reescritura" || libroEntero) return [];
  const rutas = [...new Set([...(abiertoRuta ? [abiertoRuta] : []), ...adjuntos])];
  return rutas
    .map((ruta) => ({ ruta, seguro: ruta === abiertoRuta && abiertoTexto != null }))
    .filter((r) => !r.seguro || (abiertoTexto ?? "").length > CARACTERES_POR_ADJUNTO);
}

/**
 * Las iniciales de quien ha entrado, para su burbuja en el chat. Estaban escritas a mano como «RB» y
 * todos los autores salían con las de Rais (Arturo, piloto de Windows, 16-sep-2026). Manda el nombre
 * de la cuenta; si no hay, el email antes de la @, partido por puntos y guiones.
 */
export function inicialesDe(u: { firstName?: string | null; lastName?: string | null; email?: string | null }): string {
  const nombre = [u.firstName, u.lastName].map((x) => (x ?? "").trim()).filter(Boolean);
  const partes = nombre.length ? nombre : (u.email ?? "").split("@")[0].split(/[._\-+]+/).filter(Boolean);
  const letras = partes.map((p) => p.replace(/[^\p{L}]/gu, "")[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return letras || "TÚ";
}

/**
 * Dónde sale un texto en un hilo: cada mensaje que lo contiene, con un poco de contexto a cada lado.
 * Sin distinguir mayúsculas ni tildes («reescritura» encuentra «Reescritura» y «capitulo» encuentra
 * «capítulo»). Lo más reciente primero, que es lo que se busca casi siempre.
 */
export function coincidencias(
  mensajes: { texto: string; mio: boolean }[], busca: string, contexto = 30,
): { antes: string; encontrado: string; despues: string; mio: boolean }[] {
  const plano = (t: string) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const q = plano(busca.trim());
  if (q.length < 3) return [];
  const fuera: { antes: string; encontrado: string; despues: string; mio: boolean }[] = [];
  for (const m of [...mensajes].reverse()) {
    const texto = m.texto.replace(/\s+/g, " ");
    // NFD + quitar marcas conserva la longitud solo si no hay caracteres compuestos raros; se
    // busca en la versión plana y se corta en la original por la misma posición, que en español
    // (tildes y eñes precompuestas) coincide porque se normaliza carácter a carácter.
    const planoTexto = Array.from(texto).map((c) => plano(c) || c).join("");
    const i = planoTexto.indexOf(q);
    if (i < 0) continue;
    const ini = Math.max(0, i - contexto), fin = Math.min(texto.length, i + q.length + contexto);
    fuera.push({
      antes: (ini > 0 ? "…" : "") + texto.slice(ini, i),
      encontrado: texto.slice(i, i + q.length),
      despues: texto.slice(i + q.length, fin) + (fin < texto.length ? "…" : ""),
      mio: m.mio,
    });
  }
  return fuera;
}

/**
 * EL CAMINO (Fase 12, R-09): en qué punto está el libro, etapa por etapa.
 *
 * Las etapas RECOMIENDAN, no bloquean (Rais, 17-sep): cada una dice su estado y, si falta lo de
 * antes, qué se recomienda hacer primero. Nunca impide pulsar; saltarse una etapa queda en la
 * bitácora. Con un motor que no da un dato, la etapa sale «sin datos», nunca un error.
 */
export type IdEtapa = "diagnostico" | "plan" | "pasadas" | "correccion" | "publicacion";
export type EstadoEtapa = "hecha" | "en_curso" | "sin_empezar" | "sin_datos";

export interface HechosDelLibro {
  manuscrito: boolean;
  informe: { nota: number | null; fecha: string | null } | null | undefined;   // undefined = no se sabe
  plan: { aprobado: boolean; total: number; hechas: number } | null | undefined;
  versiones: number | undefined;                                               // builds medidos
  correcciones: number | undefined;                                            // encargos al corrector
  publicacion: number | undefined;                                             // entregables de Publicación
}

export interface Etapa { id: IdEtapa; nombre: string; estado: EstadoEtapa; detalle: string; recomendado: string | null }

export const ETAPAS: { id: IdEtapa; nombre: string; agente: string }[] = [
  { id: "diagnostico", nombre: "Diagnóstico", agente: "coach" },
  { id: "plan", nombre: "Plan", agente: "editor" },
  { id: "pasadas", nombre: "Pasadas", agente: "editor" },
  { id: "correccion", nombre: "Corrección", agente: "copyeditor" },
  { id: "publicacion", nombre: "Publicación", agente: "publicar" },
];

export function estadoCamino(h: HechosDelLibro): Etapa[] {
  const sinMs = h.manuscrito ? null : "sube o elige tu manuscrito";
  const nota = (n: number | null) => (n == null ? "hecho" : `nota ${String(n).replace(".", ",")}`);
  const e = (id: IdEtapa, estado: EstadoEtapa, detalle: string, recomendado: string | null): Etapa =>
    ({ id, nombre: ETAPAS.find((x) => x.id === id)!.nombre, estado, detalle, recomendado });

  const diag = h.informe === undefined ? e("diagnostico", "sin_datos", "sin datos", sinMs)
    : h.informe ? e("diagnostico", "hecha", nota(h.informe.nota), null)
    : e("diagnostico", "sin_empezar", "sin empezar", sinMs);

  const plan = h.plan === undefined ? e("plan", "sin_datos", "sin datos", null)
    : h.plan?.aprobado ? e("plan", "hecha", "aprobado", null)
    : h.plan ? e("plan", "en_curso", "sin aprobar", null)
    : e("plan", "sin_empezar", "sin empezar", h.informe ? null : sinMs ?? "haz antes el diagnóstico");

  const p = h.plan;
  const pas = p === undefined ? e("pasadas", "sin_datos", "sin datos", null)
    : p && p.total > 0 && p.hechas >= p.total ? e("pasadas", "hecha", `${p.total} de ${p.total}`, null)
    : p && p.hechas > 0 ? e("pasadas", "en_curso", `pasada ${p.hechas + 1} de ${p.total}`, null)
    : e("pasadas", "sin_empezar", p?.total ? `0 de ${p.total}` : "sin empezar", p?.aprobado ? null : sinMs ?? "aprueba antes el plan");

  const pendientesPasadas = !!p && p.hechas < p.total;
  const cor = h.correcciones === undefined ? e("correccion", "sin_datos", "sin datos", null)
    : h.correcciones > 0 ? e("correccion", pendientesPasadas ? "en_curso" : "hecha", `${h.correcciones} ${h.correcciones === 1 ? "corrección" : "correcciones"}`, pendientesPasadas ? "acaba antes las pasadas" : null)
    : e("correccion", "sin_empezar", "sin empezar", sinMs ?? (pendientesPasadas || !p ? "acaba antes las pasadas" : null));

  const pub = h.publicacion === undefined ? e("publicacion", "sin_datos", "sin datos", null)
    : h.publicacion > 0 ? e("publicacion", "en_curso", `${h.publicacion} ${h.publicacion === 1 ? "entregable" : "entregables"}`, null)
    : e("publicacion", "sin_empezar", "sin empezar", sinMs ?? (h.correcciones ? null : "corrige antes"));

  return [diag, plan, pas, cor, pub];
}

/**
 * Lo que cuesta un informe del libro entero (R-14): todo el manuscrito de entrada y un informe largo
 * de salida (3.000–6.000 palabras ≈ 9.000 tokens). Sin precio del modelo, null: no se inventa.
 */
export const TOKENS_SALIDA_INFORME = 9000;

/**
 * El coste de un ENCARGO (25-sep-2026) antes de pulsar: el motor dice cuántos caracteres viajarían
 * (el oficio, el contexto del proyecto y el trozo del libro) y aquí se pasan a tokens con ~4
 * caracteres por token, más una salida de documento. Sin precio del modelo, null: no se inventa.
 */
export const TOKENS_SALIDA_ENCARGO = 7000;
export function costeEncargo(caracteres: number, modelo: { entrada: number | null; salida: number | null } | undefined): number | null {
  if (!modelo || modelo.entrada == null || modelo.salida == null || !(caracteres > 0)) return null;
  return (Math.round(caracteres / 4) / 1e6) * modelo.entrada + (TOKENS_SALIDA_ENCARGO / 1e6) * modelo.salida;
}
/** Lo que de verdad lee el motor de un manuscrito: hasta 500.000 caracteres, unas 83.000 palabras. */
export const PALABRAS_MAX_LIBRO = 83000;
export function costeInforme(palabras: number, modelo: { entrada: number | null; salida: number | null } | undefined): number | null {
  if (!modelo || modelo.entrada == null || modelo.salida == null) return null;
  const entrada = Math.round(Math.min(Math.max(0, palabras), PALABRAS_MAX_LIBRO) * TOKENS_POR_PALABRA);
  return (entrada / 1e6) * modelo.entrada + (TOKENS_SALIDA_INFORME / 1e6) * modelo.salida;
}

/**
 * Dónde está el libro, en una píldora: manuscrito · versión · pasada (Rais, 17-sep: «que las
 * numeraciones queden siempre claras»). Sale del NOMBRE del documento —la misma convención que usa
 * el motor para numerar builds— y del plan. Lo que no se sabe no se inventa: sin número en el
 * nombre, `version` es null y la píldora lo dice.
 */
export interface PuntoDelLibro { manuscrito: string | null; version: string | null; pasada: string | null }
export function puntoDelLibro(doc: string | null, plan: HechosDelLibro["plan"]): PuntoDelLibro {
  const n = doc ? nombreDeRuta(doc) : "";
  const pad = (x: string) => x.padStart(2, "0");
  let manuscrito: string | null = null, version: string | null = null;
  let m = n.match(/-m(\d+)-v(\d+)[^/\\]*\.docx$/i);
  if (m) { manuscrito = `m${pad(m[1])}`; version = `v${pad(m[2])}`; }
  else if ((m = n.match(/^cap-(\d+)-v(\d+)\.docx$/i))) { manuscrito = `capítulo ${Number(m[1])}`; version = `v${m[2]}`; }
  else if ((m = n.match(/-b(\d+)[^/\\]*\.docx$/i))) { version = `b${pad(m[1])}`; }
  const pasada = !plan || !plan.total ? null
    : plan.hechas >= plan.total ? `${plan.total} pasadas hechas`
    : `pasada ${plan.hechas + 1} de ${plan.total}`;
  return { manuscrito, version, pasada };
}

/**
 * El título de un capítulo para el mapa (revisión del 17-sep: salían «15», «1», «2 (parte 1/2)»).
 * Un número solo es «Capítulo N»; un romano, «Capítulo XII»; y el tramo de un capítulo largo que el
 * lector partió se dice con palabras. El resto, tal cual, sin las almohadillas de Markdown.
 */
export function tituloDeCapitulo(titulo: string): string {
  let t = String(titulo ?? "").replace(/^#+\s*/, "").trim();
  let parte = "";
  const m = t.match(/\s*\(parte (\d+)\/(\d+)\)\s*$/i);
  if (m) { parte = ` · parte ${m[1]} de ${m[2]}`; t = t.slice(0, m.index).trim(); }
  if (/^\d{1,3}\s*[.\-–—:]?$/.test(t)) t = `Capítulo ${parseInt(t, 10)}`;
  else if (/^[IVXLCDM]{1,7}\s*[.\-–—:]?$/.test(t)) t = `Capítulo ${t.replace(/[.\-–—:\s]+$/, "")}`;
  return (t || "Sin título") + parte;
}

/**
 * Dónde está en el libro la cita de un hallazgo del informe (T-33).
 *
 * El informe señala doce cosas con su cita literal, y hasta hoy esa cita no llevaba a ningún sitio:
 * el autor tenía que buscarla a mano en su manuscrito. Aquí se localiza y se devuelve con su
 * contexto y su capítulo, para poder leerla en su sitio y convertirla en una pasada del plan.
 *
 * La búsqueda es tolerante: tildes, comillas tipográficas, rayas y espacios cambian según de dónde
 * venga el texto (Word, el modelo, un copiar y pegar), y una comparación literal fallaría casi
 * siempre. Se compara carácter a carácter SIN cambiar la longitud, para poder cortar el original
 * por la misma posición — el mismo criterio que `coincidencias`.
 */
const LLANO: Record<string, string> = { "“": '"', "”": '"', "«": '"', "»": '"', "‘": "'", "’": "'", "—": "-", "–": "-", " ": " " };
const llano = (t: string) =>
  Array.from(t).map((c) => {
    if (LLANO[c]) return LLANO[c];
    if (/\s/.test(c)) return " ";
    const n = c.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    return n.length === 1 ? n : c.toLowerCase();
  }).join("");

export interface Pasaje {
  antes: string; cita: string; despues: string;
  capitulo: number | null; tituloCapitulo: string | null;
}

export function pasajeEnLibro(
  texto: string, cita: string,
  capitulos: { title: string; texto: string }[] = [],
  contexto = 320,
): Pasaje | null {
  const q = llano(String(cita ?? "").trim());
  if (q.length < 12) return null;                    // una cita demasiado corta casaría en cualquier parte
  const t = String(texto ?? "");
  let i = llano(t).indexOf(q);
  // Si no casa entera, se prueba con su primera mitad: el modelo a veces recorta o alarga el final.
  if (i < 0 && q.length > 40) i = llano(t).indexOf(q.slice(0, Math.floor(q.length / 2)));
  if (i < 0) return null;
  const fin = Math.min(t.length, i + q.length);
  // Los capítulos reales se numeran como en una pasada: «Preliminares» no cuenta.
  const reales = capitulos.filter((c) => c.title !== "Preliminares");
  const dentro = reales.findIndex((c) => llano(c.texto).includes(q.slice(0, Math.min(q.length, 60))));
  return {
    antes: t.slice(Math.max(0, i - contexto), i),
    cita: t.slice(i, fin),
    despues: t.slice(fin, fin + contexto),
    capitulo: dentro >= 0 ? dentro + 1 : null,
    tituloCapitulo: dentro >= 0 ? reales[dentro].title : null,
  };
}

/**
 * Los dos maestros más cercanos a una voz (Estilos III, T-37).
 *
 * Se cuenta SOLO sobre los ejes que están puntuados: meter los que el lector dejó en «no lo sé»
 * como si fueran un 5 fabricaría un parecido que nadie ha juzgado. Y con menos de tres ejes no se
 * compara con nadie, porque un parecido sacado de dos números es una ocurrencia, no un dato.
 */
export function maestrosCerca(
  mios: Record<string, { puntuacion: number | null }>,
  maestros: { id: string; nombre: string; huella: Record<string, number>; provisional?: boolean }[],
  cuantos = 2,
) {
  const ids = Object.keys(mios ?? {}).filter((k) => Number.isInteger(mios[k]?.puntuacion as number));
  if (ids.length < 3) return [];
  return [...(maestros ?? [])]
    .map((m) => ({
      m,
      d: ids.reduce((s, k) => s + Math.abs((mios[k].puntuacion as number) - (m.huella?.[k] ?? 5)), 0) / ids.length,
    }))
    .sort((a, b) => a.d - b.d || a.m.id.localeCompare(b.m.id))
    .slice(0, cuantos)
    .map((x) => ({ ...x.m, distancia: Math.round(x.d * 10) / 10 }));
}

/**
 * EL NOMBRE CON SU VERSIÓN (Rais, 21-sep: «versionamos el nombre del fichero»). Un manuscrito que
 * entra en la carpeta de un proyecto se guarda como `<Libro>-m01-v01.docx`, y así cada pasada crea la
 * siguiente —v02, v03…— sin adivinar. Se quita lo que parezca una versión suelta («DMVM-3v12»,
 * «Libro v7», «Libro_final») para no acabar con «DMVM-3v12-m01-v01». Si ya sigue el estándar
 * (`-m01-v03` o `-b07`), se deja como está. Solo .docx: un .md o un .txt no pasa por las pasadas.
 *
 * Devuelve `null` si no hay que cambiar nada.
 */
export function nombreConVersion(nombre: string, enLaCarpeta: string[] = []): string | null {
  const n = String(nombre ?? "").trim();
  if (!/\.docx$/i.test(n)) return null;
  const base = n.replace(/\.docx$/i, "");
  const dos = (x: number) => String(x).padStart(2, "0");
  const parte = (x: string) => {
    const s2 = nombreDeRuta(x).replace(/\.docx$/i, "");
    let m = s2.match(/^(.+?)[-_ ]+m(\d{1,2})[-_ ]?v(\d{1,3})$/i);
    if (m) return { libro: m[1], m: Number(m[2]), v: Number(m[3]) };
    m = s2.match(/^(.+?)[-_ ]+(\d{1,2})[-_ ]?v(\d{1,3})$/i);
    if (m) return { libro: m[1], m: Number(m[2]), v: Number(m[3]) };
    m = s2.match(/^(.+?)[-_ ]+v(\d{1,3})$/i);
    if (m) return { libro: m[1], m: 1, v: Number(m[2]) };
    return null;
  };
  const suyo = parte(base);
  const libro = (suyo?.libro ?? base.replace(/[-_ ]+(final|definitivo|revisado|ultimo|último)$/i, ""))
    .trim().replace(/\s+/g, "-") || "Manuscrito";
  // Si el proyecto YA tiene versiones de este libro, la copia entra como la SIGUIENTE (Rais,
  // 22-sep: «tiene que ser otra versión»). Nunca se reutiliza el número de una que ya está.
  const mismas = enLaCarpeta.map(parte).filter((p): p is NonNullable<typeof p> =>
    !!p && p.libro.trim().replace(/\s+/g, "-").toLowerCase() === libro.toLowerCase());
  if (mismas.length) {
    const m = suyo?.m ?? Math.max(...mismas.map((x) => x.m));
    const delManuscrito = mismas.filter((x) => x.m === m);
    // El que ya trae su número lo conserva; solo si ESE número ya está en la carpeta pasa a la
    // siguiente versión libre, porque dos ficheros no pueden ser la misma versión.
    const ocupada = (v: number) => delManuscrito.some((x) => x.v === v);
    const v = suyo && !ocupada(suyo.v)
      ? suyo.v
      : (delManuscrito.length ? Math.max(...delManuscrito.map((x) => x.v)) : 0) + 1;
    return `${libro}-m${dos(m)}-v${dos(v)}.docx`;
  }
  if (/-m\d+-v\d+[^/\\]*\.docx$/i.test(n) || /-b\d+[^/\\]*\.docx$/i.test(n)) return null;
  if (suyo) return `${libro}-m${dos(suyo.m)}-v${dos(suyo.v)}.docx`;
  return `${libro}-m01-v01.docx`;
}

/** «manuscrito 3, versión 12», dicho del nombre que se le ha puesto. Para el aviso de la subida. */
export function versionDelNombre(nombre: string): string {
  const m = String(nombre ?? "").match(/-m(\d+)-v(\d+)/i);
  return m ? `manuscrito ${Number(m[1])}, versión ${Number(m[2])}` : "manuscrito 1, versión 1";
}

/**
 * LO QUE UNA VERSIÓN DEJA VIEJO (Rais, 22-sep: «si una versión nueva afecta a algo que está a
 * medias, hay que explicarlo: vas a tener que regenerar el informe de lectura, que a su vez
 * regenerará el plan…»). Cada entrada es un cambio del motor que hace que lo producido ANTES ya no
 * valga igual, con qué hay que rehacer y en qué orden. `desde` es cuándo se pudo tener esa versión:
 * lo hecho antes, caduca. Una entrada por versión que lo necesite; las demás no tocan nada.
 */
export interface Caducidad { version: string; desde: string; que: string; pasos: string[] }
export const CADUCAN: Caducidad[] = [
  {
    version: "1.6.9",
    desde: "2026-09-21T21:35:00Z",
    que: "tu manuscrito se lee ahora sin los códigos del índice de Word ni el texto borrado con control de cambios. Lo que se hizo antes los leyó como si fueran parte del libro.",
    pasos: [
      "Si tu manuscrito tiene cambios sin aceptar, decídelos antes en Word (aceptar o rechazar): lo que siga pendiente de borrar ya no cuenta como texto.",
      "Rehaz el informe de lectura y, si los tienes, los del editor, los lectores beta y el de estilo.",
      "Con los informes nuevos, vuelve a preparar el plan de reescritura: el de antes se escribió con los viejos.",
      "En Estilos, vuelve a traer los ejes del informe de lectura nuevo.",
    ],
  },
];
/** La última versión que deja viejo algo hecho en `fecha`, o null si sigue valiendo. */
export function caducadoPor(fecha?: string | null): Caducidad | null {
  const t = fecha ? Date.parse(fecha) : NaN;
  if (Number.isNaN(t)) return null;
  return [...CADUCAN].reverse().find((c) => t < Date.parse(c.desde)) ?? null;
}

/**
 * QUÉ ES CADA FICHERO de la carpeta, dicho en una línea (Rais, 22-sep: «no se ve cuál es el fichero
 * que tengo que utilizar para los cambios»). La carpeta acaba con el manuscrito, sus versiones y
 * todo lo que el Studio entrega, y de un vistazo no se sabe cuál se abre en Word.
 */
export function papelDeFichero(nombre: string, doc?: string | null): string | null {
  const n = nombreDeRuta(nombre);
  if (doc && nombre === doc) return "tu manuscrito · el que se lee y del que salen las versiones";
  // Lo que ENTREGA el Studio se mira primero: «informe-lectura-DMVM-m01-v01.docx» acaba en algo que
  // parece una versión del libro, y sin este orden salía etiquetado como el manuscrito.
  if (/^informe-lectura-comparado/i.test(n)) return "informe de lectura comparado · para leer";
  if (/^informe-lectura|^informe-global/i.test(n)) return "informe de lectura · para leer";
  if (/^informe-/i.test(n)) return "informe · para leer";
  if (/^registro-reescritura/i.test(n)) return "registro de la reescritura · para leer";
  if (/^plan-reescritura/i.test(n)) return "plan de reescritura · para leer";
  if (/^(biblia|escaleta)\./i.test(n)) return "documento de trabajo de tu libro";
  if (/\.json$/i.test(n)) return "datos del Studio · no hay que abrirlo";
  const version = (x: string) => {
    const m = nombreDeRuta(x).match(/^(.*?)-m(\d+)-v(\d+)\.docx$/i) ?? nombreDeRuta(x).match(/^(.*?)-b(\d+)\.docx$/i);
    return m ? { libro: m[1].toLowerCase(), n: Number(m[3] ?? m[2]) } : null;
  };
  const a = version(n), b = doc ? version(doc) : null;
  if (a && b && a.libro === b.libro) {
    return a.n > b.n
      ? "versión nueva de tu libro · ábrela en Word: los cambios vienen marcados"
      : "versión anterior de tu libro";
  }
  if (a) return "una versión de tu libro";
  return null;
}

/**
 * UNA CARPETA QUE NO PUEDE SER UN PROYECTO (la misma regla que aplica el motor, R-06): la carpeta
 * personal, Descargas, Documentos, Escritorio u OneDrive enteros, o cualquiera con más de 60
 * documentos. Devuelve por qué, para decírselo al autor con sus palabras.
 *
 * Rais, 23-sep: al volver a studio.humanink.io el Studio recuperó «Downloads» como proyecto —626
 * documentos— y parecía que había que empezar de cero.
 */
export const MAX_DOCUMENTOS_PROYECTO = 60;
export function carpetaImposible(dir: string | null, cuantos = 0): string | null {
  if (!dir) return null;
  const n = nombreDeRuta(dir).toLowerCase();
  const sueltas = ["downloads", "descargas", "documents", "documentos", "desktop", "escritorio", "onedrive", "icloud drive"];
  if (sueltas.includes(n)) return `«${nombreDeRuta(dir)}» es una carpeta del sistema, no la de un libro`;
  if (/^\/users\/[^/]+\/?$/i.test(dir) || /^[A-Z]:\\Users\\[^\\]+\\?$/i.test(dir)) return "esa es tu carpeta personal entera";
  if (cuantos > MAX_DOCUMENTOS_PROYECTO) return `ahí dentro hay ${cuantos} documentos: un libro no vive en una carpeta así`;
  return null;
}

/**
 * LA ÚLTIMA VERSIÓN DE TU LIBRO en la carpeta (Rais, 23-sep: «yo no tengo que escoger el manuscrito
 * 2, esto lo tiene que hacer automáticamente»). Devuelve el nombre de la versión posterior a la
 * abierta —misma serie, número mayor—, o null si la abierta ya es la última.
 */
export function ultimaVersionDe(doc: string | null, ficheros: { name: string }[] = []): string | null {
  if (!doc) return null;
  const parte = (n: string) => {
    const m = nombreDeRuta(n).match(/^(.*?)-m(\d+)-v(\d+)\.docx$/i) ?? nombreDeRuta(n).match(/^(.*?)-b(\d+)\.docx$/i);
    return m ? { libro: m[1].toLowerCase(), m: m[3] ? Number(m[2]) : 1, v: Number(m[3] ?? m[2]) } : null;
  };
  const versiones = ficheros
    .filter((f) => !/^(informe|plan|registro)-/i.test(f.name))
    .map((f) => ({ name: f.name, p: parte(f.name) }))
    .filter((x) => x.p);
  const laMayor = (suyas: typeof versiones) => suyas.sort((a, b) => (b.p!.m - a.p!.m) || (b.p!.v - a.p!.v))[0]?.name ?? null;
  const mio = parte(doc);
  if (mio) return laMayor(versiones.filter((x) => x.p!.libro === mio.libro && x.p!.m === mio.m && x.p!.v > mio.v));

  // EL MANUSCRITO TAL COMO LLEGÓ, SIN NÚMERO EN EL NOMBRE (César, 4-oct: corrigió «PRIMAVERA 2009.docx»,
  // salió «primavera-2009-m01-v01.docx» y el Studio se quedó en el original, ofreciendo corregirlo otra
  // vez). Es el caso de todo autor nuevo, y aquí «sin numeración no se adivina» lo dejaba fuera. El
  // motor nombra esa primera versión como la CARPETA del proyecto; se acepta también el nombre del
  // propio documento. Cualquier otro libro numerado de la carpeta sigue sin mezclarse.
  // Como lo nombra el motor (`builds.mjs`): solo los espacios pasan a guion; un «_» se queda como está.
  const slug = (t: string) => t.trim().replace(/\s+/g, "-").toLowerCase();
  const nombre = nombreDeRuta(doc);
  if (!/\.docx$/i.test(nombre)) return null;
  const tronco = nombre.replace(/\.docx$/i, "");
  // Con versión, pero fuera del estándar: «DMVM-3v12» sigue en «DMVM-m03-v13»; «Libro v7», en «Libro-m01-v08».
  const libre = tronco.match(/^(.+?)[-_ ]+(\d{1,2})[-_ ]?v(\d{1,3})$/i) ?? tronco.match(/^(.+?)[-_ ]+()v(\d{1,3})$/i);
  if (libre) {
    const suyo = { libro: slug(libre[1]), m: libre[2] ? Number(libre[2]) : 1, v: Number(libre[3]) };
    return laMayor(versiones.filter((x) => x.p!.libro === suyo.libro && x.p!.m === suyo.m && x.p!.v > suyo.v));
  }
  const carpeta = doc.split(/[\\/]/).filter(Boolean).at(-2) ?? "";
  const suyos = new Set([slug(tronco), slug(carpeta)].filter(Boolean));
  return laMayor(versiones.filter((x) => /-m\d+-v\d+\.docx$/i.test(x.name) && suyos.has(x.p!.libro)));
}

/** ¿Dos ficheros son el mismo libro? Se compara la serie, no la versión: v01 y v02 son el mismo. */
export function mismoLibro(a: string | null, b: string | null): boolean {
  if (!a || !b) return true;
  const serie = (x: string) => nombreDeRuta(x).replace(/\.docx$/i, "")
    .replace(/-p\d+$/i, "")
    .replace(/-m(\d+)-v\d+$/i, "-m$1")
    .replace(/-b\d+$/i, "")
    .toLowerCase();
  return serie(a) === serie(b);
}

/** Lo que el colaborador miró por su cuenta para contestar (CH-4, motor 1.6.27). */
export interface Consulta { herramienta: string; resumen: string }

/**
 * «Ha mirado: Capítulo 2 (1.234 palabras) · notas (11)». Sin esto el autor no sabe si la respuesta
 * sale del libro o de lo que el colaborador supone. Null si no miró nada.
 */
export function frasesDeConsultas(consultas: Consulta[] | undefined | null): string | null {
  const l = (Array.isArray(consultas) ? consultas : []).map((c) => String(c?.resumen ?? "").trim()).filter(Boolean);
  if (!l.length) return null;
  const unicas = [...new Set(l)];
  return `Ha mirado: ${unicas.slice(0, 6).join(" · ")}${unicas.length > 6 ? ` y ${unicas.length - 6} más` : ""}`;
}

/**
 * Una ficha de encargo que propone un colaborador en el chat (CH-4 fase B, motor 1.6.28). El motor
 * no lanza nada: dice qué, sobre qué y por qué; el coste lo pone la pantalla con las mismas cuentas
 * que la pantalla de ese trabajo, y solo se gasta si el autor pulsa «Hazlo».
 */
export interface Propuesta {
  id: string; accion: string; titulo: string; alcance: string; porque: string; deja: string;
  agente: string; forma: "informe" | "reescritura" | "pasada" | "fase" | "encargo" | "medida";
  /** forma «medida»: la operación de la bitácora cuyo coste real, en este libro, hace de estimación. */
  op?: string;
  palabras: number; version: string; estado: "propuesta" | "encargado" | "descartado";
  informe?: string; pasada?: number; encargo?: string; trabajo?: string | null;
  /** forma «fase» (M-5): el número de la fase del plan que se propone hacer entera. */
  fase?: number;
}

/**
 * ¿La ficha es de OTRO manuscrito que el abierto? (equipo rojo 1.6.28). Su coste, su alcance y su
 * porqué eran de aquel; lanzarla sobre este sería encargar a ciegas. Sin manuscrito abierto, no se sabe.
 */
export const fichaDeOtroManuscrito = (version: string, doc: string | null | undefined): boolean =>
  !!doc && nombreDeRuta(doc) !== version;

/**
 * Lo que cuesta una ficha, en dólares; null si no se sabe (sin precio del modelo, o sin el dato que
 * hace falta: las palabras de la pasada o los caracteres del encargo, que se piden al motor).
 */
export function costeDePropuesta(
  p: Pick<Propuesta, "forma" | "palabras">,
  modelo: { id?: string; entrada: number | null; salida: number | null } | undefined,
  { palabrasPasada = null, caracteresEncargo = null, medido = null }: { palabrasPasada?: number | null; caracteresEncargo?: number | null; medido?: number | null } = {},
): number | null {
  // El informe de lectura y el plan no tienen cuenta de palabras que valga (equipo rojo 1.6.28): lo que
  // costaron antes en este libro, o nada.
  if (p.forma === "medida") return medido;
  if (!modelo) return null;
  if (p.forma === "informe") return costeInforme(p.palabras, modelo);
  if (p.forma === "reescritura") return estimarCoste(p.palabras, modelo, undefined, "reescritura").coste;
  // Una pasada o una fase entera: las palabras que el MOTOR dice que van a viajar, no las del libro.
  if (p.forma === "pasada" || p.forma === "fase") return palabrasPasada == null ? null : estimarCoste(palabrasPasada, modelo, undefined, "reescritura").coste;
  if (p.forma === "encargo") return caracteresEncargo == null ? null : costeEncargo(caracteresEncargo, modelo);
  return null;
}

/** Los tipos de obra que necesitan género antes del informe (la ficción), como en el motor. */
export const necesitaGenero = (tipo: string | null | undefined) => tipo === "novela" || tipo === "relatos";

/** Cómo lee el motor el género guardado (`genero_leido`, motor 1.6.29): la pantalla dice eso mismo. */
export type GeneroLeido = { id: string; nombre: string; lente: string | null; descripcion: string | null; a_mano?: boolean };

/** «Otro» se guarda con su prefijo y el motor lo lee así siempre: nunca lo reconvierte en un género de mercado. */
export const guardarOtro = (descripcion: string) => `otro: ${String(descripcion ?? "").trim()}`.slice(0, 120);

/**
 * El género guardado, para la pantalla. Manda lo que dice el motor (`leido`), que es con lo que se lee
 * el libro: equipo rojo 1.6.29, la pantalla decía «Otro» mientras el informe leía con la lente de un
 * género de mercado. Sin el dato del motor (uno anterior), se deduce sin adivinar: un id de la lista,
 * «otro: …», o un texto de antes de la lista, que se enseña tal cual. `null` si no hay género.
 */
export function generoGuardado(
  valor: string | null | undefined,
  lista: { id: string; nombre: string; lente: string | null }[],
  leido?: GeneroLeido | null,
): (GeneroLeido & { a_mano: boolean }) | null {
  const v = String(valor ?? "").trim();
  if (!v) return null;
  if (leido) return { ...leido, a_mano: !!leido.a_mano };
  const g = lista.find((x) => x.id === v);
  if (g) return { ...g, descripcion: null, a_mano: false };
  const otro = v.match(/^otro\s*:\s*([\s\S]*)$/i);
  if (otro) return { id: "otro", nombre: "Otro", lente: null, descripcion: otro[1].trim() || null, a_mano: false };
  return { id: "otro", nombre: "Otro", lente: null, descripcion: v, a_mano: true };
}

/** El género en una frase, como lo lee el motor. */
export function generoEnPalabras(g: (GeneroLeido & { a_mano?: boolean }) | null): string | null {
  if (!g) return null;
  if (g.id === "otro") return g.descripcion ? `Otro: «${g.descripcion}»` : "Otro, sin describir";
  return g.a_mano && g.descripcion ? `${g.nombre} (lo escribiste como «${g.descripcion}»)` : g.nombre;
}

/** Una parte del libro que la pasada dejó como estaba: el modelo volvió cortado o vacío. */
export type SinTocar = { capitulo: string; motivo: "cortada" | "vacia" };

/**
 * Lo que la pasada o la corrección dejó sin tocar, en una frase; null si no dejó nada. Sin esto,
 * «Corrección hecha» callaba que un capítulo no se había corregido (prueba del 26-sep: Sonnet 5 con
 * esfuerzo xhigh agotó su espacio de respuesta razonando y devolvió medio capítulo).
 */
export function frasesSinTocar(lista: SinTocar[] | undefined | null): string | null {
  const l = Array.isArray(lista) ? lista : [];
  if (!l.length) return null;
  const nombres = l.map((x) => x.capitulo).filter(Boolean);
  const cortadas = l.filter((x) => x.motivo === "cortada").length;
  const cuales = nombres.length ? ` (${nombres.slice(0, 4).join(", ")}${nombres.length > 4 ? ` y ${nombres.length - 4} más` : ""})` : "";
  return `${l.length === 1 ? "Una parte se ha quedado" : `${l.length} partes se han quedado`} como estaba${l.length === 1 ? "" : "n"}${cuales}: `
    + (cortadas
      ? "el modelo se quedó sin espacio de respuesta a mitad, y un texto cortado habría borrado el resto. No se ha escrito nada en ellas ni se ha guardado para reutilizar. Si vuelves a lanzarlo, se piden de nuevo; si se repite, quita el esfuerzo extra en Modelos o prueba otro modelo."
      : "el modelo devolvió una respuesta vacía. Si vuelves a lanzarlo, se piden de nuevo.");
}

// ── LAS ESCENAS DEL LIBRO (M-3, 3-oct-2026) ─────────────────────────────────────────────────────

type EscenaVista = { n: number; capitulo: number; capitulo_titulo: string | null; palabras: number; por: string };

/** Las escenas, agrupadas por capítulo y en su orden: como se leen en la pantalla. */
export function escenasPorCapitulo<T extends EscenaVista>(escenas: T[]): { capitulo: number; titulo: string; escenas: T[] }[] {
  const fuera: { capitulo: number; titulo: string; escenas: T[] }[] = [];
  for (const e of escenas ?? []) {
    const ultimo = fuera[fuera.length - 1];
    if (ultimo && ultimo.capitulo === e.capitulo) ultimo.escenas.push(e);
    else fuera.push({ capitulo: e.capitulo, titulo: e.capitulo_titulo || `Capítulo ${e.capitulo}`, escenas: [e] });
  }
  return fuera;
}

/** De dónde sale el mapa de escenas, dicho para el autor. */
export function origenDeEscenas(fuente: string | null | undefined): string {
  return fuente === "separadores" ? "las separa tu propio texto"
    : fuente === "lectura" ? "salen de leer el libro capítulo a capítulo"
    : fuente === "autor" ? "con tus cambios"
    : "una por capítulo";
}

/** «68 escenas en 22 capítulos». */
export function cuentaDeEscenas(escenas: EscenaVista[]): string {
  const n = (escenas ?? []).length, caps = new Set((escenas ?? []).map((e) => e.capitulo)).size;
  return `${n} ${n === 1 ? "escena" : "escenas"} en ${caps} ${caps === 1 ? "capítulo" : "capítulos"}`;
}

/**
 * Lo que cuesta dividir el libro: una llamada corta por capítulo, que lee el capítulo y devuelve
 * números de párrafo. null si el modelo no trae precio.
 */
export function costeDeDividir(palabras: number, capitulos: number, modelo: { entrada: number | null; salida: number | null } | undefined): number | null {
  if (!modelo || modelo.entrada == null || modelo.salida == null) return null;
  const caps = Math.max(1, Math.round(capitulos) || 1);
  // Por capítulo: su texto con los párrafos numerados (un poco más que el texto), la instrucción y una respuesta de pocas líneas.
  const entrada = Math.round(Math.max(0, palabras) * TOKENS_POR_PALABRA * 1.1) + caps * 350;
  const salida = caps * 200;
  return (entrada / 1e6) * modelo.entrada + (salida / 1e6) * modelo.salida;
}

/** Los cortes guardados que ya no se encuentran en el texto: se dice cuáles, o null si no falta ninguno. */
export function avisoDeEscenasPerdidas(perdidos: { arranque: string | null; cap: string | null }[] | null | undefined): string | null {
  const l = perdidos ?? [];
  if (!l.length) return null;
  const donde = [...new Set(l.map((x) => x.cap).filter(Boolean))] as string[];
  return `${l.length === 1 ? "Una escena ya no empieza donde la dejaste" : `${l.length} escenas ya no empiezan donde las dejaste`}: `
    + `ese texto cambió o se quitó${donde.length ? ` (${listaConY(donde.slice(0, 3))}${donde.length > 3 ? " y más" : ""})` : ""}. `
    // «Se ha unido a la de antes» a secas era falso cuando la pasada la había QUITADO (equipo rojo, 4-oct).
    + `${l.length === 1 ? "Si sigue en el libro, ahora va unida" : "Las que sigan en el libro van ahora unidas"} a la de antes: `
    + `${l.length === 1 ? "pártela" : "pártelas"} otra vez si quieres.`;
}

/**
 * Por dónde va el plan, o "" si el motor no lo dice. SOLO con un motor que manda por dónde va
 * («pensando el plan · quedan unos 9 min»): ese es el que lo pide en directo y no lo corta a los diez
 * minutos. A un motor anterior no se le promete una espera que él mismo va a cortar (equipo rojo, 4-oct).
 */
export function esperaDelPlan(trabajo: { estado?: string; progreso?: { fase?: string } | null } | null | undefined): string {
  const fase = trabajo?.estado === "en_curso" ? String(trabajo.progreso?.fase ?? "").trim() : "";
  if (!fase) return "";
  return `Ahora: ${fase}. Primero lo piensa y después lo escribe.`;
}

/**
 * El avance de un trabajo cuyo motor lo MIDE (hoy, el plan: Rais, 4-oct, «una barra de progreso que se
 * adapte a la situación»): de 0 a 100. `null` si no lo mide —un motor anterior, u otro trabajo que
 * cuenta pasos—: ahí no se pinta un porcentaje que nadie ha calculado.
 */
export function porcentajeDe(trabajo: { estado?: string; progreso?: { hecho: number; total: number; fase?: string } | null } | null | undefined): number | null {
  const p = trabajo?.progreso;
  if (trabajo?.estado !== "en_curso" || !p || p.total !== 100 || !p.fase) return null;
  return Math.max(0, Math.min(100, Math.round(p.hecho)));
}

/** Cuánto va a tardar el plan, dicho antes de pulsar; "" si el motor no lo estima. */
export function loQueTardaElPlan(estimacion: { minutos?: number } | null | undefined): string {
  const m = Math.round(Number(estimacion?.minutos));
  if (!Number.isFinite(m) || m < 1) return "";
  return m === 1 ? "Tardará alrededor de un minuto." : `Tardará unos ${m} minutos${m >= 5 ? "; puedes cerrar el Studio mientras tanto" : ""}.`;
}

/**
 * Los capítulos que la lectura no pudo leer, o null si se leyeron todos. Antes el libro quedaba
 * «dividido» con ese capítulo entero y nadie lo decía (equipo rojo, 3-oct).
 */
export function avisoDeCapitulosSinLeer(titulos: string[] | null | undefined): string | null {
  const l = (titulos ?? []).filter(Boolean);
  if (!l.length) return null;
  const cuales = l.length > 3 ? `${l.slice(0, 3).join(", ")} y ${l.length - 3} más` : listaConY(l);
  return l.length === 1
    ? `No se pudo leer un capítulo (${cuales}): por ahora cuenta como una sola escena.`
    : `No se pudieron leer ${l.length} capítulos (${cuales}): por ahora cada uno cuenta como una sola escena.`;
}

// ── UNA FASE ENTERA (M-5, 3-oct-2026) ───────────────────────────────────────────────────────────

/** Lo que va a mandar una pasada de la fase, dicho en una línea. */
export function lineaDePasadaDeFase(p: { palabras: number; llamadas: number; gratis?: number; perdidas?: number; modo?: string; por?: string; sin_capitulos?: boolean }): string {
  // No es que no gaste: es que no se puede hacer (equipo rojo, 3-oct: salía «no gasta: solo quita»).
  if (p.sin_capitulos) return "sus capítulos no coinciden con los del manuscrito";
  const aviso = p.perdidas ? ` · ${p.perdidas === 1 ? "una operación ya no encuentra su escena" : `${p.perdidas} operaciones ya no encuentran su escena`}` : "";
  if (!p.llamadas) {
    // No llama al modelo: o solo quita, o deja dicho lo que el autor tiene que escribir.
    return (p.modo === "indicacion" ? "no gasta: te deja indicado lo que tienes que escribir tú" : "no gasta: solo quita") + aviso;
  }
  return `~${p.palabras.toLocaleString("es-ES")} palabras` + aviso;
}

/** Lo que devuelve el motor al terminar una fase. */
export interface ResultadoDeFase {
  build?: string; fase?: number; area?: string; aplicadas?: number; delta?: number; sin_ancla?: number; sin_atribuir?: number;
  pasadas?: { n: number; titulo: string; cambios: number; delta: number; revisor?: string }[];
  sin_cambios?: { n: number; titulo: string }[];
  sin_sitio?: { pasada: number; n: number; motivo: string; nota: string | null }[];
  /** Los cambios que el motor de Word no supo dónde poner (o no quiso: una tabla, el índice): por qué. */
  sin_ancla_cuales?: { pasada: number | null; motivo: string; texto: string }[];
  sin_tocar?: SinTocar[]; prosa_quitada?: { quitados?: number; devueltos?: number };
  en_tablero?: { propia: string | null; enlazadas: string[] };
  /** Cambios a los que han contribuido varias pasadas: llevan el revisor de la fase. */
  compartidos?: number; revisor_de_la_fase?: string;
}

/**
 * Cómo decidir en Word los cambios de una fase, dicho sin prometer de más (equipo rojo, 3-oct): cada
 * pasada es un revisor, pero un párrafo que tocaron DOS no se puede aceptar «solo de una». Esos llevan
 * el revisor de la fase y se deciden uno a uno.
 */
export function comoDecidirEnWord(r: Pick<ResultadoDeFase, "compartidos" | "revisor_de_la_fase" | "pasadas">): string {
  const varias = (r.pasadas ?? []).length > 1;
  const base = varias
    ? "Acepta o rechaza los cambios. Cada pasada firma como un revisor distinto: en Revisar → Mostrar marcas → Personas concretas puedes ver, aceptar o rechazar los de una sola pasada."
    : "Acepta o rechaza los cambios (Revisar → Aceptar / Rechazar).";
  const n = r.compartidos ?? 0;
  if (!n) return base;
  return `${base} ${n === 1 ? "Hay un cambio en un párrafo que tocaron dos pasadas: lleva" : `Hay ${n} cambios en párrafos que tocaron dos pasadas: llevan`} `
    + `el revisor «${r.revisor_de_la_fase ?? "HumanInk · fase"}», para que ${n === 1 ? "lo" : "los"} decidas aparte.`;
}

/**
 * Lo que hay que decirle al autor de una fase recién hecha, además de «ya está»: lo que NO salió como
 * se esperaba. Frases enteras, en el orden en que importan; lista vacía si todo fue limpio.
 */
export function avisosDeFaseHecha(r: ResultadoDeFase | null | undefined): string[] {
  if (!r) return [];
  const f: string[] = [];
  const nada = r.sin_cambios ?? [];
  if (nada.length) {
    f.push(nada.length === 1
      ? `La pasada ${nada[0].n} («${nada[0].titulo}») no propuso ningún cambio: queda hecha, sin marcas.`
      : `${nada.length} pasadas no propusieron ningún cambio (${listaConY(nada.map((x) => `la ${x.n}`))}): quedan hechas, sin marcas.`);
  }
  const sitio = r.sin_sitio ?? [];
  if (sitio.length) {
    f.push(`${sitio.length === 1 ? "Una operación no encontró su escena y se ha quedado" : `${sitio.length} operaciones no encontraron su escena y se han quedado`} como nota tuya, para que decidas qué hacer con ${sitio.length === 1 ? "ella" : "ellas"}.`);
  }
  if (r.sin_ancla) {
    // Por qué, con las palabras del motor: casi siempre es una tabla o el índice, que no se tocan a propósito.
    const motivos = [...new Set((r.sin_ancla_cuales ?? []).map((x) => x.motivo).filter(Boolean))];
    f.push(`${r.sin_ancla === 1 ? "Un cambio no encontró" : `${r.sin_ancla} cambios no encontraron`} su sitio en el documento y no ${r.sin_ancla === 1 ? "se ha escrito" : "se han escrito"}`
      + (motivos.length ? ` (${motivos.slice(0, 2).join("; ")}).` : "."));
  }
  if (r.sin_atribuir) {
    f.push(`${r.sin_atribuir === 1 ? "Un cambio lleva" : `${r.sin_atribuir} cambios llevan`} el revisor de la fase y no el de una pasada: no se pudo saber de cuál ${r.sin_atribuir === 1 ? "era" : "eran"}.`);
  }
  const cortadas = frasesSinTocar(r.sin_tocar);
  if (cortadas) f.push(cortadas);
  const prosa = fraseProsaQuitada(r.prosa_quitada);
  if (prosa) f.push(prosa);
  return f;
}

/** Lo leído, en una frase para el autor. */
export function frasesDeVuelta(r: VueltaLeida): string {
  const leidas = r.leidas ?? [];
  const de = leidas.length > 1 ? `De ${leidas.length} versiones (${leidas[0].build.replace(/\.docx$/i, "")} a ${leidas[leidas.length - 1].build.replace(/\.docx$/i, "")})` : `De ${r.entregada}`;
  const t = r.total;
  return `${de}: ${t.aceptadas} aceptados tal cual, ${t.retocadas} retocados por ti, ${t.rechazadas} rechazados`
    + (t.suyas ? `, ${t.suyas} cambiados por texto tuyo.` : ".")
    + (t.sin_efecto ? ` Otros ${t.sin_efecto} la pasada los quitaba y los volvía a poner igual: no cuentan.` : "")
    + ` ${r.cuenta.resueltas} ${r.cuenta.resueltas === 1 ? "nota cerrada" : "notas cerradas"} con prueba`
    + `${r.cuenta.devueltas ? `, ${r.cuenta.devueltas} ${r.cuenta.devueltas === 1 ? "vuelve" : "vuelven"} a por decidir porque rechazaste su cambio` : ""}`
    + `${r.cuenta.descartadas ? `, ${r.cuenta.descartadas} ${r.cuenta.descartadas === 1 ? "pasada del plan que rechazaste entera pasa" : "pasadas del plan que rechazaste enteras pasan"} a descartadas` : ""}`
    + `${r.cuenta.siguen ? `, ${r.cuenta.siguen} siguen esperando` : ""}.`;
}

/**
 * Dónde está una nota, en palabras: su capítulo, los capítulos de la pasada que la escribió o el
 * libro entero. La nota de una pasada del plan no tiene cita ni un solo capítulo (R0-6).
 */
export function lugarDeNota(n: { ancla?: { capitulo: number | null; titulo_capitulo: string | null } | null; capitulos?: number[] }): string {
  if (n.ancla?.titulo_capitulo) return tituloDeCapitulo(n.ancla.titulo_capitulo);
  if (n.ancla?.capitulo) return `Capítulo ${n.ancla.capitulo}`;
  const c = n.capitulos ?? [];
  if (c.length === 1) return `Capítulo ${c[0]}`;
  if (c.length) return `Capítulos ${c.slice(0, -1).join(", ")} y ${c[c.length - 1]}`;
  return "Todo el libro";
}

/**
 * Lo que una pasada del plan dejó en el tablero (R0-6): su propia nota, con los cambios que no
 * contestaban a ninguna, y las notas que ya tenías y contestaba. `null` si no dejó nada o si el
 * motor es anterior y no lo dice.
 */
export function frasesDelTablero(x: { nota?: string | null; notas?: string[] } | null | undefined): string | null {
  const ya = x?.notas ?? [];
  if (!x?.nota && !ya.length) return null;
  const partes = [
    x?.nota ? `su propia nota (${x.nota}) con los cambios que no contestaban a ninguna` : "",
    ya.length ? `${ya.length === 1 ? "una nota que ya tenías" : `${ya.length} notas que ya tenías`} (${ya.join(", ")})` : "",
  ].filter(Boolean);
  return `En el tablero de notas: ${partes.join(" y ")}.`;
}

/**
 * Por qué esperan las versiones que aún no se han leído, agrupadas por motivo: «v02, v03: Word la
 * tiene abierta». Sin esto la tarjeta decía «se lee sola al guardar» aunque nunca fuera a leerse.
 */
export function motivosDeEspera(versiones: { build: string; motivo: string | null }[]): string[] {
  const por = new Map<string, string[]>();
  for (const v of versiones) {
    if (!v.motivo) continue;
    const corto = v.build.replace(/\.docx$/i, "").replace(/^.*-(v\d+|b\d+)$/i, "$1");
    por.set(v.motivo, [...(por.get(v.motivo) ?? []), corto]);
  }
  return [...por.entries()].map(([m, bs]) => `${bs.length > 3 ? `${bs.slice(0, 3).join(", ")} y ${bs.length - 3} más` : bs.join(", ")}: ${m}`);
}

// ── El formato del chat, como en Claude (CH-1, 26-sep) ─────────────────────────────────────────
// Los colaboradores contestan en Markdown y el chat enseñaba los asteriscos. Aquí se lee a una
// estructura (bloques y trozos de línea) que la pantalla pinta con elementos de React: nunca se
// mete HTML crudo, así que lo que escriba el modelo no puede colar nada en la página.

export type Trozo =
  | { t: "texto"; v: string }
  | { t: "negrita"; hijos: Trozo[] }
  | { t: "cursiva"; hijos: Trozo[] }
  | { t: "codigo"; v: string }
  | { t: "enlace"; v: string; href: string };

export type BloqueMd =
  | { t: "p"; trozos: Trozo[] }
  | { t: "h"; nivel: number; trozos: Trozo[] }
  | { t: "lista"; ordenada: boolean; items: Trozo[][] }
  | { t: "cita"; trozos: Trozo[] }
  | { t: "tabla"; cabecera: Trozo[][]; filas: Trozo[][][] }
  | { t: "codigo"; v: string }
  | { t: "raya" };

// Topes (equipo rojo 1.6.24): 50.000 asteriscos seguidos desbordaban la pila, y el mensaje, guardado
// en el hilo, rompía la conversación cada vez que se abría. Por encima de esto, texto tal cual.
const PROFUNDIDAD_MAX = 3;
const LINEA_MAX = 20000;

/** Los trozos de una línea: **negrita**, *cursiva*, `código` y [enlaces](https://…). */
export function trozosDeLinea(linea: string, profundidad = 0): Trozo[] {
  if (profundidad > PROFUNDIDAD_MAX || linea.length > LINEA_MAX) return linea ? [{ t: "texto", v: linea }] : [];
  const fuera: Trozo[] = [];
  let texto = "";
  const soltar = () => { if (texto) { fuera.push({ t: "texto", v: texto }); texto = ""; } };
  let i = 0;
  while (i < linea.length) {
    const resto = linea.slice(i);
    // Tres o más * o _ seguidos no son formato: se quedan como están, de una vez.
    const tira = resto.match(/^([*_])\1{2,}/);
    if (tira) { texto += tira[0]; i += tira[0].length; continue; }
    const codigo = resto.match(/^`([^`]+)`/);
    if (codigo) { soltar(); fuera.push({ t: "codigo", v: codigo[1] }); i += codigo[0].length; continue; }
    const negrita = resto.match(/^(\*\*|__)(?=\S)([\s\S]*?\S)\1(?!\1)/);
    if (negrita && (negrita[1] === "**" || !/\w/.test(linea[i - 1] ?? ""))) {
      soltar(); fuera.push({ t: "negrita", hijos: trozosDeLinea(negrita[2], profundidad + 1) }); i += negrita[0].length; continue;
    }
    const cursiva = resto.match(/^([*_])(?=\S)([^*_]*?\S)\1(?![*_\w])/);
    if (cursiva && !/\w/.test(linea[i - 1] ?? "")) {
      soltar(); fuera.push({ t: "cursiva", hijos: trozosDeLinea(cursiva[2], profundidad + 1) }); i += cursiva[0].length; continue;
    }
    const enlace = resto.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/);
    if (enlace) { soltar(); fuera.push({ t: "enlace", v: enlace[1], href: enlace[2] }); i += enlace[0].length; continue; }
    texto += linea[i];
    i++;
  }
  soltar();
  return fuera;
}

const celdas = (linea: string) => linea.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => trozosDeLinea(c.trim()));

/** Los bloques de un mensaje en Markdown. Lo que no se reconoce queda como párrafo, tal cual. */
export function bloquesDeMarkdown(texto: string): BloqueMd[] {
  const lineas = String(texto ?? "").replace(/\r\n?/g, "\n").split("\n");
  const fuera: BloqueMd[] = [];
  let parrafo: string[] = [];
  const cerrar = () => { if (parrafo.length) { fuera.push({ t: "p", trozos: trozosDeLinea(parrafo.join("\n")) }); parrafo = []; } };
  for (let i = 0; i < lineas.length; i++) {
    const l = lineas[i];
    if (/^\s*```/.test(l)) {
      cerrar();
      const cuerpo: string[] = [];
      i++;
      while (i < lineas.length && !/^\s*```/.test(lineas[i])) cuerpo.push(lineas[i++]);
      fuera.push({ t: "codigo", v: cuerpo.join("\n") });
      continue;
    }
    if (!l.trim()) { cerrar(); continue; }
    // Sin cuantificadores anidados: con 50.000 # al final, la expresión anterior tardaba 3 s.
    const h = l.length <= 2000 ? l.match(/^(#{1,6})\s+(.*)$/) : null;
    if (h) { cerrar(); fuera.push({ t: "h", nivel: h[1].length, trozos: trozosDeLinea(h[2].replace(/\s+#+\s*$/, "").trim()) }); continue; }
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(l)) { cerrar(); fuera.push({ t: "raya" }); continue; }
    if (l.length <= 2000 && (lineas[i + 1] ?? "").length <= 2000 && /^\s*\|.*\|\s*$/.test(l) && /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(lineas[i + 1] ?? "")) {
      cerrar();
      const cabecera = celdas(l);
      const filas: Trozo[][][] = [];
      i += 2;
      while (i < lineas.length && /^\s*\|.*\|\s*$/.test(lineas[i])) filas.push(celdas(lineas[i++]));
      i--;
      fuera.push({ t: "tabla", cabecera, filas });
      continue;
    }
    const lista = l.match(/^\s*(?:([-*•+])|(\d+)[.)])\s+(.*)$/);
    if (lista) {
      cerrar();
      const ordenada = !!lista[2];
      const items: Trozo[][] = [trozosDeLinea(lista[3])];
      while (i + 1 < lineas.length) {
        const m = lineas[i + 1].match(/^\s*(?:([-*•+])|(\d+)[.)])\s+(.*)$/);
        if (!m || !!m[2] !== ordenada) break;
        items.push(trozosDeLinea(m[3]));
        i++;
      }
      fuera.push({ t: "lista", ordenada, items });
      continue;
    }
    const cita = l.match(/^\s*>\s?(.*)$/);
    if (cita) {
      cerrar();
      const partes = [cita[1]];
      while (i + 1 < lineas.length && /^\s*>/.test(lineas[i + 1])) partes.push(lineas[++i].replace(/^\s*>\s?/, ""));
      fuera.push({ t: "cita", trozos: trozosDeLinea(partes.join("\n")) });
      continue;
    }
    parrafo.push(l);
  }
  cerrar();
  return fuera;
}

// ── La bitácora con las decisiones de Word (R0-11, 27-sep) ─────────────────────────────────────

export interface ViaDecision {
  id: "chat" | "word";
  titulo: string;
  aceptado: number; editado: number; descartado: number;
  /** Las decididas: la base de las barras. */
  decididas: number;
  /** Lo que hay que decir debajo, en palabras. */
  avisos: string[];
}

/**
 * Las decisiones del autor, por dónde las tomó: en el chat y los encargos, o en Word sobre los
 * cambios de las pasadas. `[]` con un motor anterior, que no las separa: entonces la pantalla sigue
 * como estaba. Una vía sin nada que contar no sale.
 */
export function viasDeDecision(r: {
  porVia?: { chat: { aceptado: number; editado: number; descartado: number; sin_decidir: number };
             word: { aceptado: number; editado: number; descartado: number; texto_propio: number; lecturas: number } };
  en_word?: { sin_leer: number } | null;
} | null | undefined): ViaDecision[] {
  if (!r?.porVia) return [];
  const { chat, word } = r.porVia;
  const fuera: ViaDecision[] = [];
  const dChat = chat.aceptado + chat.editado + chat.descartado;
  if (dChat || chat.sin_decidir) {
    fuera.push({ id: "chat", titulo: "En el chat y los encargos", aceptado: chat.aceptado, editado: chat.editado, descartado: chat.descartado, decididas: dChat,
      avisos: chat.sin_decidir ? [`${chat.sin_decidir} ${chat.sin_decidir === 1 ? "propuesta sigue" : "propuestas siguen"} sin decidir: puedes decidirlas aquí abajo.`] : [] });
  }
  const dWord = word.aceptado + word.editado + word.descartado;
  const esperan = r.en_word?.sin_leer ?? 0;
  if (dWord || esperan) {
    const avisos: string[] = [];
    if (word.texto_propio) avisos.push(`${word.texto_propio === 1 ? "Una vez" : `${word.texto_propio} veces`} pusiste texto tuyo en lugar de lo propuesto: para la IA cuenta como descartado.`);
    if (esperan) avisos.push(`${esperan} ${esperan === 1 ? "cambio espera" : "cambios esperan"} todavía en Word: se cuentan cuando los decidas y guardes.`);
    fuera.push({ id: "word", titulo: "En Word, los cambios de las pasadas", aceptado: word.aceptado, editado: word.editado, descartado: word.descartado, decididas: dWord, avisos });
  }
  return fuera;
}

/** Una lectura de Word, en una línea: lo que hiciste con los cambios de esa versión. */
export function lineaDeLectura(meta: Record<string, unknown> | null | undefined): string {
  const n = (k: string) => Number(meta?.[k] ?? 0) || 0;
  const partes = [
    `${n("aceptadas")} ${n("aceptadas") === 1 ? "aceptado" : "aceptados"}`,
    `${n("retocadas")} ${n("retocadas") === 1 ? "retocado" : "retocados"}`,
    `${n("rechazadas")} ${n("rechazadas") === 1 ? "rechazado" : "rechazados"}`,
    ...(n("suyas") ? [`${n("suyas")} con texto tuyo`] : []),
  ];
  const notas = n("resueltas") ? ` · ${n("resueltas")} ${n("resueltas") === 1 ? "nota cerrada" : "notas cerradas"}` : "";
  return partes.join(" · ") + notas;
}

/**
 * El mensaje de error del motor, venga como texto o con la forma de OpenRouter (`{ message }`). Desde
 * el motor 1.6.31 los rechazos de OpenRouter llegan así, y leerlos como texto enseñaba «[object Object]»
 * (UAT del 28-sep-2026).
 */
export function textoDeError(d: unknown): string | null {
  const e = (d as { error?: unknown } | null)?.error;
  if (typeof e === "string") return e;
  const m = e && typeof e === "object" ? (e as { message?: unknown }).message : null;
  return typeof m === "string" && m ? m : null;
}

/** «a», «a» y «b», «a», «b» y «c»: una lista para una frase. */
export function listaConY(cosas: string[]): string {
  if (cosas.length <= 1) return cosas[0] ?? "";
  return `${cosas.slice(0, -1).join(", ")} y ${cosas[cosas.length - 1]}`;
}

/**
 * Capítulos escritos sin estilo de título (UAT del 28-sep: en NEMI 169, «Capítulo Diez» se perdía
 * dentro del Nueve y la pantalla lo daba por bueno). null si no hay nada que decir.
 */
export function avisoSinEstilo(sinEstilo: { texto: string }[] | undefined | null): string | null {
  if (!sinEstilo?.length) return null;
  const n = sinEstilo.length;
  const q = sinEstilo.slice(0, 4).map((c) => `«${c.texto}»`);
  const lista = n > 4 ? `${q.join(", ")} y ${n - 4} más` : listaConY(q);
  return `${lista} ${n === 1 ? "parece un capítulo pero está escrito" : "parecen capítulos pero están escritos"} como texto normal, sin estilo de título: aquí ${n === 1 ? "cuenta" : "cuentan"} dentro del capítulo anterior. Ponle${n === 1 ? "" : "s"} el estilo de título en Word, guarda y vuelve a abrir esta pantalla.`;
}

/** Cuántos capítulos hay de verdad: «Preliminares» (lo de antes del primer título) no es uno (UAT del 28-sep). */
export const capitulosReales = <T extends { title: string }>(caps: T[]): T[] => caps.filter((c) => c.title !== "Preliminares");

/**
 * CH-6: lo que se dice cuando un trabajo largo termina mientras el autor hace otra cosa, y adónde
 * lleva «Verlo». `destino` es una sección del Studio; null si no hay una pantalla propia.
 */
export function avisoDeTrabajo(t: { tipo: string; titulo: string; estado: string; error?: string | null; reanudable?: boolean; resultado?: Record<string, unknown> | null }):
  { clase: "hecho" | "fallido"; texto: string; destino: string | null; ruta: string | null } {
  const DESTINO: Record<string, string> = {
    "informe-global": "diagnostico", "informe-editor": "diagnostico", "informe-beta": "diagnostico", "informe-estilo": "diagnostico",
    "informe-mercado": "diagnostico", diagnostico: "diagnostico",
    "informe-comparado": "diagnostico", plan: "plan", pasada: "pasadas", operaciones: "pasadas", medir: "versiones",
    correccion: "correccion", maquetacion: "publicacion", fase: "pasadas",
  };
  const destino = DESTINO[t.tipo] ?? null;
  // Lo que no tiene pantalla propia (un encargo que propuso el coach) se enseña en su carpeta: antes
  // el aviso decía «Terminado» y no había forma de llegar al fichero (equipo rojo, 3-oct).
  const ruta = !destino && t.estado === "hecho" && typeof t.resultado?.ruta === "string" && t.resultado.ruta ? t.resultado.ruta : null;
  if (t.estado === "hecho") return { clase: "hecho", texto: `Terminado: ${t.titulo}.`, destino, ruta };
  if (t.estado === "interrumpido") {
    return { clase: "fallido", destino, ruta,
      texto: `Se ha cortado: ${t.titulo}. ${t.reanudable ? "Puedes retomarlo desde su pantalla sin pagar otra vez lo ya hecho." : "Puedes volver a lanzarlo desde su pantalla."}` };
  }
  const motivo = (t.error ?? "").trim();
  return { clase: "fallido", destino, ruta, texto: `No se ha podido terminar: ${t.titulo}.${motivo ? ` ${motivo.length > 220 ? motivo.slice(0, 219) + "…" : motivo}` : ""}` };
}

/**
 * P-04: lo que va a pasar al volver a una versión anterior, en frases, ANTES de pulsar. Lo mismo que
 * hace el motor: copia la versión como la siguiente, no borra nada, y reabre plan y notas posteriores.
 */
export function frasesDeVolver(p: {
  a: string; vA: number; vUltima: number; nueva: string; vNueva: number; entre: number;
  pasadas: { titulo: string | null; n: number }[]; notas: unknown[];
}): string[] {
  const f = [`Se crea la v${p.vNueva} («${p.nueva}»), igual que la v${p.vA}, y pasa a ser tu manuscrito.`];
  if (p.entre === 1) f.push(`La v${p.vUltima} se queda en la carpeta y en la curva: no se borra nada.`);
  else if (p.entre > 1) f.push(`De la v${p.vA + 1} a la v${p.vUltima} se quedan en la carpeta y en la curva: no se borra nada.`);
  const np = p.pasadas.length;
  const nombres = listaConY(p.pasadas.map((x) => `«${x.titulo ?? `pasada ${x.n}`}»`));
  f.push(np === 0 ? "El plan no cambia."
    : np === 1 ? `La pasada ${nombres} del plan vuelve a pendiente: su trabajo estaba en una versión posterior.`
    : `${np} pasadas del plan vuelven a pendiente (${nombres}): su trabajo estaba en versiones posteriores.`);
  const nn = p.notas.length;
  f.push(nn === 0 ? "Ninguna nota cambia."
    : `${nn === 1 ? "Una nota vuelve" : `${nn} notas vuelven`} a «por decidir», porque el cambio que ${nn === 1 ? "la" : "las"} cerraba ya no está.`);
  return f;
}

/**
 * UAT del 28-sep (H1): una llave de OpenRouter mal copiada o una cuenta sin saldo se descubrían al
 * primer encargo, que fallaba. El Studio lo pregunta al abrir y lo dice arriba. null: nada que decir
 * (buena, sin llave —de eso ya avisa la bienvenida— o sin red, que no es culpa de la llave).
 */
export function avisoDeClave(c: { estado: string; sinCredito?: boolean } | null | undefined): string | null {
  if (!c) return null;
  if (c.estado === "rechazada") return "OpenRouter no reconoce tu llave: puede estar mal copiada o borrada. Pégala otra vez en Ajustes antes de encargar nada.";
  if (c.estado === "buena" && c.sinCredito) return "Tu cuenta de OpenRouter no tiene saldo: los encargos fallarán hasta que añadas crédito en openrouter.ai → Credits.";
  return null;
}
